import { createContext } from "preact";
import { useContext } from "preact/hooks";

/**
 * Every word the console shows, in Turkish and English.
 *
 * The English dictionary is typed as the Turkish one, so a key added to one
 * and forgotten in the other is a compile error rather than a blank label in
 * production. Values that need numbers or names are functions.
 */

export type Lang = "tr" | "en";

const tr = {
  common: {
    loading: "Yükleniyor…",
    offline: "Sunucuya ulaşılamıyor. Bağlantı gelince panel kendiliğinden açılacak.",
    retry: "Tekrar dene",
    save: "Kaydet",
    saveChanges: "Değişiklikleri kaydet",
    cancel: "Vazgeç",
    add: "Ekle",
    edit: "Düzenle",
    delete: "Sil",
    close: "Kapat",
    download: "İndir",
    saveFailed: "Kaydedilemedi.",
    actionFailed: "İşlem yapılamadı.",
    unexpected: "Beklenmeyen bir hata oldu.",
    optional: "isteğe bağlı",
    site: "Site",
    appTitle: "AgentlyDesk",
    appTitleUnread: (n: number) => `(${n}) AgentlyDesk`,
    readOnly: "Bu ayarları yalnızca yöneticiler değiştirebilir. Sizde görüntüleme izni var.",
    createSiteFirst: "Önce bir site oluşturun.",
    sitesFailed: "Siteler alınamadı.",
    confirm: "Onayla",
    typeToConfirm: (text: string) => `Onaylamak için “${text}” yazın`,
    connectionLost: "Bağlantı koptu, yeniden bağlanılıyor. Bu sırada gelen mesajlar bağlantı gelince yüklenecek.",
    exportCsv: "CSV indir",
    exporting: "Hazırlanıyor…",
    exportFailed: "Dışa aktarılamadı.",
    allSites: "Tüm siteler",
    tag: "Etiket",
    showMore: "Daha fazla göster",
  },
  time: {
    now: "şimdi",
    minutes: (n: number) => `${n} dk`,
    hours: (n: number) => `${n} sa`,
    days: (n: number) => `${n} g`,
    today: "Bugün",
    yesterday: "Dün",
    ago: (x: string) => `${x} önce`,
  },
  status: {
    pending: "Bekliyor",
    open: "Açık",
    resolved: "Çözüldü",
    closed: "Kapalı",
  },
  visitor: (id: number) => `Ziyaretçi #${id}`,
  visitors: {
    location: "Konum",
    deviceCol: "Cihaz",
    devices: { mobile: "Mobil", tablet: "Tablet", desktop: "Masaüstü" },
    ip: "IP adresi",
    perSite: "Site başına ziyaretçi",
    allSites: "Tüm siteler",
    title: "Ziyaretçiler",
    intro: "Şu anda sitelerinizde olan kişiler. Liste kendiliğinden yenilenir. Gezinti kaydedilmez; kişi sayfadan çıkınca listeden de çıkar.",
    empty: "Şu anda sitede kimse yok.",
    emptyHint: "Sohbet kutusunun yüklü olduğu bir sayfa açıldığında burada görünür.",
    loadFailed: "Ziyaretçiler alınamadı.",
    who: "Ziyaretçi",
    page: "Bulunduğu sayfa",
    since: "Geldi",
    unknownPage: "Bilinmiyor",
    tabs: (n: number) => `${n} sekme`,
    openChat: "Sohbeti aç",
    start: "Sohbet başlat",
    startTitle: (name: string) => `${name} ile sohbet başlat`,
    startBody: "Mesajınız ziyaretçinin ekranındaki sohbet kutusunda belirir ve sohbet size atanır.",
    startLabel: "İlk mesajınız",
    startPlaceholder: "Merhaba, yardımcı olabileceğim bir şey var mı?",
    startFailed: "Sohbet başlatılamadı.",
    count: (n: number) => `${n} kişi`,
  },
  role: { owner: "Sahip", admin: "Yönetici", agent: "Operatör" },
  login: {
    title: "AgentlyDesk",
    sub: "Devam etmek için giriş yapın.",
    email: "E-posta",
    password: "Şifre",
    checking: "Kontrol ediliyor…",
    continue: "Devam et",
    twoFactorTitle: "İki adımlı doğrulama",
    recoveryHint: "Kurtarma kodlarınızdan birini girin.",
    codeHint: "Doğrulama uygulamanızdaki altı haneli kodu girin.",
    recoveryCode: "Kurtarma kodu",
    code: "Kod",
    verifying: "Doğrulanıyor…",
    signIn: "Giriş yap",
    useApp: "Uygulama kodunu kullan",
    noPhone: "Telefonuma erişemiyorum",
    enrolTitle: "İki adımlı doğrulamayı kurun",
    enrolWhy: "Bu panel tüm müşteri yazışmalarını gösterir, bu yüzden ikinci adım zorunludur.",
    qrFallback: "QR okunmuyorsa anahtarı elle girin:",
    appCode: "Uygulamadaki kod",
    finishSetup: "Kurulumu tamamla",
    codesTitle: "Kurtarma kodları",
    codesHint: "Telefonunuzu kaybederseniz giriş yapmanın tek yolu bunlar. Her biri bir kez kullanılır ve bir daha gösterilmez.",
    codesDone: "Kaydettim, panele geç",
    language: "Dil",
    forgot: "Şifremi unuttum",
    forgotTitle: "Şifrenizi sıfırlayın",
    forgotHint: "E-posta adresinizi yazın, size şifre belirleme bağlantısı gönderelim. İki adımlı doğrulamanız olduğu gibi kalır.",
    forgotSubmit: "Bağlantı gönder",
    forgotSending: "Gönderiliyor…",
    forgotSent: "Bu adres kayıtlıysa şifre belirleme bağlantısı gönderildi. Gelen kutunuza bakın; bağlantı 72 saat geçerlidir.",
    backToLogin: "Girişe dön",
  },
  nav: {
    label: "Ana menü",
    inbox: "Gelen kutusu",
    inboxWaiting: (n: number) => `Gelen kutusu, ${n} sohbet cevap bekliyor`,
    history: "Geçmiş",
    visitors: "Ziyaretçiler",
    stats: "İstatistik",
    earlyAccess: "Erken erişim",
    customers: "Müşteriler",
    settings: "Ayarlar",
    themeSystem: "Tema: sistem ayarı",
    themeLight: "Tema: açık",
    themeDark: "Tema: koyu",
    presenceOnline: "Çevrimiçi. Uzakta olarak işaretle",
    presenceAway: "Uzakta. Çevrimiçi ol",
    signOut: "Çıkış yap",
  },
  inbox: {
    title: "Gelen kutusu",
    listLabel: "Sohbetler",
    filterLabel: "Sohbet filtresi",
    tabMine: "Bana atanan",
    tabUnassigned: "Atanmamış",
    tabActive: "Tümü açık",
    tabPending: "Cevapsız",
    tabResolved: "Çözülen",
    searchPlaceholder: "Ad, e-posta veya mesajda ara",
    searchLabel: "Sohbetlerde ara",
    noResultsTitle: "Sonuç yok",
    noResultsHint: "Aramayı kısaltmayı ya da başka bir sekmeyi deneyin.",
    emptyTitle: "Burada bekleyen sohbet yok",
    emptyHint: "Yeni bir müşteri yazdığında ses çalar ve burada görünür.",
    noMessages: "Henüz mesaj yok",
    sentImage: "Resim gönderdi",
    teamPrefix: (text: string) => `Destek: ${text}`,
    waitingTitle: (when: string) => `Müşteri ${when} yazdı, cevap bekliyor`,
    verifiedTitle: "Kimliği site tarafından doğrulandı",
    assigneeTitle: (name: string) => `Atanan: ${name}`,
    unread: (n: number) => `${n} okunmamış mesaj`,
    conversationLabel: "Sohbet",
    pickTitle: "Bir sohbet seçin",
    pickHint: "Soldaki listeden bir sohbet açın. Kenarındaki renkli şerit, müşterinin ne kadar süredir cevap beklediğini gösterir.",
    typing: (name: string) => `${name} yazıyor…`,
    listFailed: "Liste alınamadı.",
    openFailed: "Sohbet açılamadı.",
    resolvedToast: "Sohbet çözüldü olarak işaretlendi.",
    reopenedToast: "Sohbet yeniden açıldı.",
    transcriptSent: (email: string) => `Döküm ${email} adresine gönderildi.`,
    banPrompt: "Engelleme nedeni (yalnızca ekip görür):",
    banDefault: "Belirtilmedi",
    bannedToast: "Ziyaretçi engellendi.",
    unbannedToast: "Engel kaldırıldı.",
    deleteConfirm:
      "Bu sohbet, içindeki tüm mesajlar ve resimler kalıcı olarak silinecek. Bu işlem geri alınamaz; yedek yoktur. Devam edilsin mi?",
    deletedToast: "Sohbet kalıcı olarak silindi.",
    purgeConfirm: (name: string) =>
      `${name} ile ilgili HER ŞEY silinecek: kişi kaydı, tüm sohbetleri, mesajları, gönderdiği resimler ve engel kaydı. Bu işlem geri alınamaz; yedek yoktur. Devam edilsin mi?`,
    purgedToast: (n: number) => `Ziyaretçi ve ${n} sohbeti kalıcı olarak silindi.`,
    draft: (name: string) => `${name} yazıyor (henüz göndermedi)`,
    assignedBy: (name: string) => `${name} size bir sohbet devretti`,
    assignedAuto: "Size yeni bir sohbet atandı",
    mentionedBy: (name: string) => `${name} sizi bir notta etiketledi`,
    handedOver: (name: string) => `Sohbet ${name} adlı kişiye devredildi.`,
    ticketCreated: (id: string) => `Destek talebi açıldı: #${id}`,
    sentFile: "Dosya gönderdi",
    loadMore: "Daha fazla sohbet göster",
    filterTag: "Etikete göre süz",
    select: "Seç",
    selectDone: "Seçimi bitir",
    selected: (n: number) => `${n} sohbet seçildi`,
    selectAll: "Listedekilerin hepsini seç",
    bulkResolve: "Çözüldü yap",
    bulkAssignMe: "Bana ata",
    bulkDelete: "Sil",
    bulkDeleteTitle: (n: number) => `${n} sohbet silinsin mi?`,
    bulkDeleteBody: "Seçilen sohbetler, içlerindeki tüm mesaj ve dosyalarla birlikte kalıcı olarak silinir. Bu işlem geri alınamaz; yedek yoktur.",
    bulkDone: (n: number) => `${n} sohbet güncellendi.`,
    bulkDeleted: (n: number) => `${n} sohbet kalıcı olarak silindi.`,
    bulkPartial: (ok: number, failed: number) => `${ok} sohbette işlem yapıldı, ${failed} sohbette yapılamadı.`,
    banTitle: "Ziyaretçiyi engelle",
    banReason: "Engelleme nedeni",
    banReasonHint: "isteğe bağlı, yalnızca ekip görür",
    banConfirm: "Engelle",
    deleteTitle: "Sohbet silinsin mi?",
    purgeTitle: "Tüm veriler silinsin mi?",
    messageDeleted: "Mesaj silindi.",
    visitorSaved: "Ziyaretçi bilgileri kaydedildi.",
    ticketErrors: {
      notConfigured: "Bu site için destek talebi adresi ayarlanmamış (Ayarlar → Siteler).",
      notIdentified: "Ziyaretçi sitede oturum açmamış; talep bir hesaba bağlanamıyor.",
      exists: "Bu sohbetten zaten bir talep açılmış.",
      inProgress: "Bu sohbetten talep şu anda açılıyor, lütfen bekleyin.",
      webhookFailed: "Site talebi kabul etmedi. Adresi ve anahtarı kontrol edin.",
      webhookSecretMissing: "Bu site için webhook anahtarı yok. Bir yönetici Ayarlar → Siteler'den oluşturmalı.",
    } as Record<string, string>,
  },
  conv: {
    back: "Listeye dön",
    removeTag: (tag: string) => `${tag} etiketini kaldır`,
    removeTagTitle: "Etiketi kaldır",
    assignedChange: (name: string) => `Atanan: ${name}. Değiştir`,
    assignSomeone: "Birine ata",
    mine: "Bende",
    assign: "Ata",
    assignMe: "Bana ata",
    unassign: "Atamayı kaldır",
    tagAdd: "Etiket ekle",
    tag: "Etiket",
    tagPlaceholder: "Etiket yazıp Enter'a basın",
    reopen: "Yeniden aç",
    resolve: "Çözüldü",
    more: "Diğer işlemler",
    transcript: "Sohbet dökümünü e-postayla gönder",
    noEmail: "Ziyaretçinin kayıtlı e-postası yok",
    info: "Müşteri bilgisi",
    readOnly: "Bu sohbet kapandı. Yazmak için yeniden açın.",
    openTicket: "Destek talebi aç",
    ticketNoWebhook: "Bu site için talep adresi ayarlanmamış",
    ticketNoIdentity: "Ziyaretçi sitede oturum açmamış",
    ticketChip: (id: string) => `Talep #${id}`,
    handoverTitle: (name: string) => `${name} adlı kişiye devret`,
    handoverNote: "Devir notu",
    handoverHint: "isteğe bağlı, sohbete iç not olarak eklenir ve kişiye bildirilir",
    handoverPlaceholder: "örn. İade istiyor, sipariş no 123",
    handoverConfirm: "Devret",
    delete: "Sohbeti kalıcı olarak sil",
    deleteAdminOnly: "Sohbeti yalnızca yöneticiler silebilir",
    loadEarlier: "Daha eski mesajları yükle",
    downloadTranscript: "Sohbet dökümünü indir (.txt)",
    editMessageTitle: "Mesajı düzenle",
    editMessageHint: "Müşteri mesajın düzenlendiğini görür.",
    deleteMessageTitle: "Bu mesaj silinsin mi?",
    deleteMessageBody: "Mesaj müşterinin ekranından da kalkar. Geri alınamaz.",
    deleteNoteBody: "İç not kalıcı olarak silinir.",
  },
  thread: {
    label: "Mesajlar",
    automatic: "Otomatik mesaj",
    team: "Destek",
    note: "İç not",
    sending: "gönderiliyor…",
    uploading: "Yükleniyor",
    edited: "düzenlendi",
    editMessage: "Mesajı düzenle",
    deleteMessage: "Mesajı sil",
  },
  composer: {
    modes: "Mesaj türü",
    reply: "Yanıt",
    note: "İç not",
    attach: "Dosya ekle (resim, PDF, metin)",
    replyPlaceholder: "Yanıtınızı yazın",
    notePlaceholder: "Yalnızca ekibin göreceği not",
    send: "Gönder",
    saveNote: "Notu kaydet",
    hintEnter: "Enter gönderir, Shift+Enter yeni satır",
    hintSlash: "/ hazır cevaplar",
    cannedLabel: "Hazır cevaplar",
    noCanned: "Henüz hazır cevap yok. Ayarlar'dan ekleyebilirsiniz.",
    noCannedMatch: "Bu kısayolla eşleşen hazır cevap yok.",
    sendFailed: "Mesaj gönderilemedi.",
    imageType: "Yalnızca PNG, JPG, GIF veya WEBP resim gönderilebilir.",
    imageTooBig: (mb: number) => `Resim ${mb} MB'tan büyük olamaz.`,
    uploadFailed: "Dosya gönderilemedi.",
    fileType: "Yalnızca resim (PNG, JPG, GIF, WEBP), PDF veya düz metin (.txt, .log) gönderilebilir.",
    fileTooBig: (mb: number) => `Dosya ${mb} MB'tan büyük olamaz.`,
    emoji: "Emoji ekle",
    suggestions: "Önerilen hazır cevaplar",
    mentionLabel: "Ekipten birini etiketle",
    noMention: "Eşleşen ekip üyesi yok.",
    hintMention: "@ ile ekipten birini çağırın",
  },
  context: {
    label: "Müşteri bilgisi",
    copyEmail: "E-postayı kopyala",
    copied: "Kopyalandı",
    verified: "✓ Hesabı doğrulandı",
    verifiedTitle: "Site, bu kişinin hesabını imzayla doğruladı",
    unverified: "Doğrulanmamış ziyaretçi",
    unverifiedTitle: "Giriş yapmamış ya da site kimliğini doğrulamadı",
    banned: "Engelli",
    siteAccount: "Site hesabı",
    now: "Şu an",
    page: "Sayfa",
    unknown: "Bilinmiyor",
    referrer: "Nereden geldi",
    conversation: "Sohbet",
    startedAt: (date: string, relative: string) => `${date} (${relative})`,
    pages: "Gezdiği sayfalar",
    pagesEmpty: "Sohbet açıldıktan sonra gezdiği sayfalar burada listelenir.",
    device: "Cihaz",
    browser: "Tarayıcı",
    os: "Sistem",
    deviceType: "Cihaz",
    country: "Ülke",
    firstVisit: "İlk ziyaret",
    lastSeen: "Son görülme",
    past: "Önceki sohbetler",
    firstChat: "Bu ilk sohbeti.",
    emptyPreview: "Resim ya da boş mesaj",
    actions: "İşlemler",
    ban: "Ziyaretçiyi engelle",
    unban: "Engeli kaldır",
    purge: "Tüm verilerini sil",
    purgeHint: "Kişi kaydı, tüm sohbetleri, mesajları ve resimleri kalıcı olarak silinir. Geri alınamaz.",
    stars: (n: number) => `${n} / 5 yıldız`,
    ticket: "Destek talebi",
    ticketOpened: (date: string) => `${date} tarihinde açıldı`,
    ticketOpen: "Talebi aç",
    editVisitor: "Bilgileri düzenle",
    editVisitorTitle: "Ziyaretçi bilgileri",
    editVisitorBody: "Ziyaretçi adını ya da e-postasını sohbette söylediyse buraya yazabilirsiniz.",
    visitorName: "Ad",
    visitorEmail: "E-posta",
    liveNow: "Şu an sitede",
  },
  settings: {
    title: "Ayarlar",
    sectionsLabel: "Ayar bölümleri",
    groupAccount: "Hesabınız",
    groupWidget: "Sohbet kutusu",
    groupAdmin: "Yönetim",
    tabProfile: "Profil",
    tabNotifications: "Bildirimler",
    tabSecurity: "Güvenlik",
    tabWidget: "Görünüm ve karşılama",
    tabCanned: "Hazır cevaplar",
    tabTriggers: "Otomatik mesajlar",
    tabHours: "Mesai saatleri",
    tabSites: "Siteler",
    tabOperators: "Ekip",
    tabSpam: "Spam koruması",
    tabAudit: "Denetim kaydı",
    tabBilling: "Plan ve fatura",
  },
  profile: {
    photo: "Fotoğraf",
    displayNameHint: "Yanıtlarınızın yanında müşteriye görünen ad. Kişi adı olsun; e-posta adresi değil.",
    preferences: "Tercihler",
    emailHint: "Giriş için kullanılır; müşterilere hiçbir zaman gösterilmez. Değiştirmek için yöneticinize başvurun.",
    seenTitle: "Müşterilerin gördüğü siz",
    seenIntro: "Yanıtlarınızın yanında bu ad ve fotoğraf görünür. E-posta adresiniz hiçbir yerde gösterilmez.",
    uploadPhoto: "Fotoğraf yükle",
    removePhoto: "Fotoğrafı kaldır",
    photoHint: "PNG, JPG, GIF veya WEBP, en fazla 2 MB",
    displayName: "Görünen ad",
    saveName: "Adı kaydet",
    nameSaved: "Adınız kaydedildi.",
    photoSaved: "Fotoğrafınız güncellendi.",
    photoTooBig: "Fotoğraf 2 MB'tan büyük olamaz.",
    account: "Hesap",
    email: "E-posta",
    role: "Rol",
    language: "Panel dili",
    languageHint: "Yalnızca sizin panelinizi değiştirir. Müşterinin gördüğü dil, sohbet kutusu ayarlarındadır.",
  },
  telegram: {
    title: "Telegram",
    intro:
      "Sohbetler bir Telegram grubuna düşsün, oradan cevap verin. Her sohbet grupta ayrı bir konu olur. " +
      "Dikkat: gruptaki herkes tüm müşteri sohbetlerini görür, gruba yalnız ekibinizi ekleyin. Bağlı bir Telegram " +
      "hesabı panel girişi kadar değerlidir; Telegram'da iki adımlı doğrulamayı açın.",
    notConfigured: "Telegram bu sunucuda ayarlı değil.",
    group: "Ekip grubu",
    groupNone: "Bağlı grup yok. Konuları (Topics) açık bir grup kurun, botu yönetici yapın (\"Konuları yönet\" yetkisiyle), sonra kodu alıp gruba yazın.",
    groupLinked: (title: string) => `Bağlı: ${title || "grup"}`,
    groupCode: "Grup kodu al",
    me: "Benim hesabım",
    meNone: "Telegram hesabınız bağlı değil. Kodu alıp gruba ya da bota özelden yazın.",
    meLinked: (name: string) => `Bağlı: ${name}`,
    meCode: "Hesabımı bağla",
    unlink: "Bağlantıyı kaldır",
    codeHint: "Telegram'a şunu yazın (15 dakika geçerli, tek kullanımlık): ",
    bot: (name: string) => `Bot: @${name}`,
    copy: "Kopyala",
    failed: "İşlem yapılamadı, tekrar deneyin.",
  },
  notify: {
    unanswered: "Cevapsız kalma uyarısı",
    unansweredHint: "Bir müşteri bu süreden uzun cevap beklerse ses ve bildirimle hatırlatır. Yalnızca size atanmış ya da kimseye atanmamış sohbetler için; her bekleyiş için bir kez.",
    unansweredOff: "Kapalı",
    unansweredMinutes: (n: number) => `${n} dakika`,
    unansweredTitle: "Cevap bekleyen müşteri",
    unansweredBody: (name: string, minutes: number) => `${name} ${minutes} dakikadır cevap bekliyor.`,
    browserTitle: "Bu tarayıcıda",
    testRow: "Deneme",
    testHint: "Açık olan ses ve masaüstü bildirimini şimdi bir kez çalar; ayarların işe yaradığını görün.",
    desktopApp: "Windows uygulaması",
    desktopAppHint: "Paneli kendi penceresinde açar: Windows açılışında başlar, saatin yanında bekler, görev çubuğunda okunmamış sayısını gösterir.",
    desktopAppDownload: "İndir (Windows)",
    title: "Bildirimler",
    intro: "Yeni bir müşteri yazdığında nasıl haber alacağınız. Bu ayarlar bu tarayıcıda saklanır.",
    sound: "Ses",
    soundHint: "Yeni sohbette ve açık olmayan bir sohbete mesaj gelince kısa bir ses çalar.",
    tabTitle: "Sekme başlığında sayı",
    tabTitleHint: "Başka sekmedeyken okunmamış mesaj sayısı sekme başlığında görünür.",
    desktop: "Masaüstü bildirimi",
    desktopHint: "Panel arka plandayken ekranın köşesinde bildirim çıkar.",
    desktopAllow: "Tarayıcıdan izin iste",
    desktopDenied: "Tarayıcı bildirim iznini reddetmiş. Adres çubuğundaki kilit simgesinden izin verebilirsiniz.",
    desktopUnsupported: "Bu tarayıcı masaüstü bildirimlerini desteklemiyor.",
    test: "Deneme bildirimi gönder",
    testTitle: "AgentlyDesk",
    testBody: "Bildirimler çalışıyor.",
    newChat: "Yeni sohbet",
    newMessage: (name: string) => `${name} yazdı`,
    image: "Resim gönderdi",
    push: "Telefon bildirimi",
    pushHint: "Panel kapalıyken de bildirim gelir. Bu cihaz için geçerlidir; her cihazda ayrı açmanız gerekir.",
    pushOn: (n: number) => `Açık — bu hesap için ${n} cihaz kayıtlı.`,
    pushEnabling: "İzin isteniyor…",
    pushFailed: "Bildirim açılamadı. Tarayıcı izin vermemiş olabilir.",
    pushDenied: "Tarayıcı bildirim iznini reddetmiş. Adres çubuğundaki kilit simgesinden izin verip tekrar deneyin.",
    pushUnsupported: "Bu tarayıcı kapalıyken bildirim göndermeyi desteklemiyor. Telefonda paneli ana ekrana ekleyip oradan açın.",
  },
  security: {
    appCodeHint: "Yeni kodlar için doğrulama uygulamanızdaki 6 haneli kodu yazın.",
    passwordTitle: "Şifre",
    passwordIntro: "Değiştirdiğinizde bu tarayıcı dışındaki tüm oturumlarınız kapanır.",
    current: "Mevcut şifre",
    next: "Yeni şifre",
    nextHint: "en az 12 karakter",
    again: "Yeni şifre (tekrar)",
    mismatch: "İki şifre aynı değil.",
    change: "Şifreyi değiştir",
    changed: "Şifreniz değişti. Diğer cihazlardaki oturumlarınız kapatıldı.",
    codesTitle: "Kurtarma kodları",
    codesIntro: "Telefonunuz kaybolursa bu kodlardan biriyle giriş yapabilirsiniz. Yeni kodlar oluşturduğunuzda eskilerin hepsi geçersiz olur.",
    codesWarn: "Bu kodlar yalnızca şimdi gösteriliyor. Yazdırın ya da şifre yöneticinize kaydedin.",
    codesDone: "Kaydettim, gizle",
    appCode: "Doğrulama uygulamasındaki 6 haneli kod",
    newCodes: "Yeni kodlar oluştur",
  },
  sites: {
    revealed: (name: string) => `${name}: gizli anahtarlar`,
    tabGeneral: "Genel",
    tabInstall: "Kurulum",
    tabKeys: "Gizli anahtarlar",
    tabProtection: "Koruma",
    tabDanger: "Tehlikeli bölge",
    installIntro: "Sohbet kutusunu sitenize koymak için gerekenler. Kodu sitenizin tüm sayfalarına ekleyin ve alan adınızı izinli listeye yazın.",
    keysIntro: "İki anahtar da yalnızca sitenizin sunucusunda durur; sayfaya ya da tarayıcıya asla konmaz. Tam değerleri yalnızca oluşturulduklarında bir kez gösterilir.",
    nameHint: "Yalnızca panelde görünür; ziyaretçiler görmez.",
    publicKeyHint: "Herkese açıktır ve kurulum kodunun içinde zaten vardır; tek başına bir yetki vermez.",
    embedHint: "Sitenizin tüm sayfalarında, </body> etiketinden hemen önce.",
    secretKeyHint: "Siteniz giriş yapmış üyelerini bu anahtarla imzalar; üyenin adı ve e-postası sohbete doğrulanmış olarak gelir.",
    webhookSecretHint: "Sohbetten destek talebi açılınca sitenize gönderdiğimiz isteği bu anahtarla imzalarız.",
    storageHint: "Sohbetlerde gönderilen resim ve dosyalar. Saklama süresi dolan sohbetlerle birlikte silinir.",
    days: "gün",
    statusOn: "Yayında",
    statusOff: "Kapalı",
    siteCount: (n: number) => `${n} site`,
    newButton: "Yeni site",
    copy: "Kopyala",
    copied: "Kopyalandı",
    emptyTitle: "Henüz site yok",
    rotated: (name: string) => `${name}: yeni kimlik anahtarı`,
    webhookRotated: (name: string) => `${name}: yeni webhook anahtarı`,
    secretWarn: "Bu anahtar yalnızca bir kez gösterilir. Sitenizin sunucu ayarlarına kaydedin, sayfaya ya da tarayıcıya asla koymayın.",
    webhookWarn: " Webhook anahtarı yalnızca size gönderilen destek talebi isteklerini doğrulamak içindir (covert: CHAT_WEBHOOK_SECRET); kimlik anahtarıyla (CHAT_SITE_SECRET) aynı yere yazmayın.",
    rotatedWarn: " Eski anahtar 24 saat daha çalışır; bu sürede yenisini yerleştirin.",
    revokedWarn: " Eski anahtar hemen iptal edildi: yenisini sitenizin sunucusuna yerleştirene kadar giriş yapmış üyeler anonim görünür.",
    webhookRevokedWarn: " Eski anahtar hemen iptal edildi: yenisini sitenizin sunucusuna yerleştirene kadar destek talepleri reddedilir.",
    savedHide: "Kaydettim, gizle",
    publicKey: "Genel anahtar",
    secretKey: "Kimlik anahtarı (gizli)",
    webhookSecret: "Webhook imza anahtarı (gizli)",
    webhookMissing: "Henüz yok. Bu anahtar oluşturulmadan sohbetten destek talebi açılamaz.",
    webhookCreate: "Webhook anahtarı oluştur",
    embed: "Sitenize eklenecek kod",
    origins: "İzinli alan adları",
    originsHint: "her satıra bir tane, örnek: https://siteniz.com",
    /** Boş liste = widget hiçbir yerde açılmaz; sunucu güvenlik gereği kapalı davranır. */
    noOriginsWarn:
      "Bu sitede henüz izinli alan adı yok. Sitenizin alan adını aşağıya ekleyene kadar widget sayfanızda yüklenmeyi reddeder — kod doğru olsa bile hiçbir şey görünmez.",
    noOriginsBadge: "alan adı yok",
    noOriginsNewHint: "Alan adını şimdi eklemezseniz widget yüklenmez; site ayarlarından sonradan da ekleyebilirsiniz.",
    originsSaved: (name: string) => `${name}: izinli alan adları kaydedildi.`,
    identity: "Yalnızca giriş yapmış üyeler",
    identityHint:
      "En güçlü koruma. Açıkken yalnızca sitenizin imzaladığı (giriş yapmış) üyeler sohbet açabilir; anonim ziyaretçiler ve sahte Origin başlığıyla gelen botlar hiç giremez.",
    rotate: "Yenile (eski anahtar 24 saat çalışır)",
    rotateLeaked: "Sızdı — hemen iptal et",
    rotateConfirm: (name: string) =>
      `${name} için kimlik anahtarı yenilensin mi? Eski anahtar 24 saat daha çalışır; bu sürede yenisini sitenizin sunucusuna yerleştirin.`,
    rotateLeakedConfirm: (name: string) =>
      `${name} için kimlik anahtarı yenilensin ve eskisi HEMEN iptal edilsin mi? Yenisini sitenizin sunucusuna yerleştirene kadar giriş yapmış üyeler anonim görünür.`,
    webhookRotateConfirm: (name: string) =>
      `${name} için webhook anahtarı yenilensin mi? Eski anahtar 24 saat daha çalışır; bu sürede yenisini sitenizin sunucusuna yerleştirin.`,
    webhookLeakedConfirm: (name: string) =>
      `${name} için webhook anahtarı yenilensin ve eskisi HEMEN iptal edilsin mi? Yenisini sitenizin sunucusuna yerleştirene kadar destek talepleri reddedilir.`,
    previousValid: (when: string) => `Eski anahtar ${when} tarihine kadar çalışmaya devam ediyor.`,
    revokePrevious: "Eski anahtarı şimdi iptal et",
    revokePreviousConfirm:
      "Eski kimlik anahtarı hemen çalışmayı bırakır. Sitenizin sunucusu hâlâ onu kullanıyorsa üyeler anonim görünür. Devam edilsin mi?",
    webhookRevokePreviousConfirm:
      "Eski webhook anahtarı hemen çalışmayı bırakır. Sitenizin sunucusu hâlâ onu kullanıyorsa destek talepleri reddedilir. Devam edilsin mi?",
    previousRevoked: (name: string) => `${name}: eski anahtar iptal edildi.`,
    anonTitle: "Anonim ziyaretçi sınırı",
    anonIntro:
      "Genel anahtar herkese açıktır ve tarayıcı dışından Origin başlığı taklit edilebilir; IP değiştiren bir bot ağı gelen kutunuzu doldurabilir. Bu sınırlar, belirlenen süre içinde kabul edilen YENİ anonim ziyaretçi ve YENİ anonim sohbet sayısını site başına sınırlar. Giriş yapmış üyeler hiçbir zaman sayılmaz ve engellenmez. Sınır dolunca anonim ziyaretçiye \"şu an yoğun, biraz sonra deneyin ya da giriş yapın\" denir. 0 = sınır yok.",
    anonVisitors: "Yeni anonim ziyaretçi",
    anonConversations: "Yeni anonim sohbet",
    anonWindow: "Süre (dakika)",
    anonSaved: (name: string) => `${name}: anonim ziyaretçi sınırı kaydedildi.`,
    anonInvalid: "Sınırlar 0 ya da daha büyük tam sayı, süre 1–1440 dakika olmalı.",
    newTitle: "Yeni site ekle",
    newIntro: "Oluşturunca size iki gizli anahtar (kimlik ve webhook) ve sitenize eklenecek tek satırlık kod verilir.",
    name: "Site adı",
    create: "Siteyi oluştur",
    ticketUrl: "Destek talebi adresi",
    ticketUrlHint: "Sohbetten talep açılınca webhook anahtarıyla imzalı istek buraya gider. covert için: https://siteniz.com/api/chat/ticket",
    ticketUrlSaved: (name: string) => `${name}: talep adresi kaydedildi.`,
    storage: "Dosya alanı",
    nameSaved: (name: string) => `Site adı “${name}” olarak kaydedildi.`,
    active: "Site açık",
    activeHint: "Kapalıyken sohbet kutusu bu sitede hiç görünmez. Geçmiş sohbetler silinmez.",
    pausedBadge: "kapalı",
    activeSaved: (name: string, on: boolean) => `${name} ${on ? "açıldı" : "kapatıldı"}.`,
    retention: "Sohbetleri saklama süresi (gün)",
    retentionHint: "bu süreden eski kapanmış sohbetler mesaj ve dosyalarıyla kendiliğinden silinir; 0 = hiç silinmez",
    retentionSaved: (name: string) => `${name}: saklama süresi kaydedildi.`,
    retentionInvalid: "Saklama süresi 0 ile 3650 gün arasında bir tam sayı olmalı.",
    deleteTitle: "Siteyi sil",
    deleteIntro:
      "Site; tüm ziyaretçileri, sohbetleri, mesajları ve dosyalarıyla birlikte kalıcı olarak silinir. Geri alınamaz. Yalnızca sohbet almayı durdurmak istiyorsanız yukarıdan siteyi kapatmanız yeterli.",
    deleteButton: "Siteyi kalıcı olarak sil",
    deleteOwnerOnly: "Siteyi yalnızca çalışma alanı sahibi silebilir.",
    deleteConfirmTitle: (name: string) => `${name} silinsin mi?`,
    deleted: (name: string) => `${name} silindi.`,
    storageUsed: (used: string, cap: string, percent: number) => `${used} / ${cap} kullanıldı (%${percent}).`,
    storageWarn:
      " Alan dolmak üzere. Dolduğunda ziyaretçiler ve operatörler bu siteye resim gönderemez; eski sohbetleri silerek ya da saklama süresini kısaltarak yer açabilirsiniz.",
  },
  widget: {
    endedShown: "Biten sohbet ziyaretçide ne kadar kalsın",
    endedShownHint: "Sohbet çözüldü ya da kapandıktan sonra bu süre geçince, geri gelen ziyaretçiye boş bir sohbet kutusu açılır ve yazdığı yeni bir sohbet olur. Eski konuşma panelde ve Geçmiş'te aynen durur. Ziyaretçi dilerse biten sohbetin altındaki düğmeyle hemen yeni sohbet de başlatabilir.",
    endedShownOptions: { none: "Hemen kaldır", "1h": "1 saat", "24h": "24 saat", "7d": "7 gün", always: "Hep göster" },
    preview: "Önizleme",
    welcome: "Karşılama",
    title: "Başlık",
    subtitle: "Alt başlık",
    greeting: "Karşılama mesajı",
    greetingHint: "sohbet boşken ilk görünen balon",
    placeholder: "Yazma alanındaki ipucu",
    quickTitle: "Hızlı yanıt butonları",
    quickIntro: (max: number) =>
      `Sohbet ilk açıldığında konu butonları olarak görünür (en fazla ${max}). Tıklanan buton ilk mesaj olarak gider ve sohbete etiket olarak eklenir.`,
    quickNone: "Henüz buton yok.",
    quickNew: "Yeni buton",
    quickExample: "örn. Ödeme",
    up: (q: string) => `${q} yukarı`,
    down: (q: string) => `${q} aşağı`,
    remove: (q: string) => `${q} sil`,
    look: "Görünüm",
    primary: "Ana renk",
    onPrimary: "Ana renk üzerindeki yazı",
    position: "Konum",
    right: "Sağ alt",
    left: "Sol alt",
    theme: "Tema",
    themeAuto: "Ziyaretçinin cihazına göre",
    themeLight: "Hep açık",
    themeDark: "Hep koyu",
    offsetX: "Kenardan uzaklık, yatay (px)",
    offsetY: "Kenardan uzaklık, dikey (px)",
    language: "Müşterinin gördüğü dil",
    langTr: "Türkçe",
    langEn: "İngilizce",
    features: "Özellikler",
    attachments: "Resim gönderme",
    attachmentsHint: "Ziyaretçiler ekran görüntüsü ve resim gönderebilir.",
    rating: "Görüşme sonunda puan iste",
    ratingHint: "Sohbet çözüldü olarak işaretlenince 1–5 yıldız sorulur.",
    typing: "“Yazıyor…” göster",
    typingHint: "Operatör yazarken ziyaretçi bunu görür.",
    sound: "Bildirim sesi",
    soundHint: "Ziyaretçinin tarayıcısında yeni mesajda kısa bir ses çalar.",
    contactAsk: "Ziyaretçiden ad ve e-posta iste",
    contactAskOff: "Sorma",
    contactAskOptional: "İsteğe bağlı — ilk mesajdan sonra sor",
    contactAskRequired: "Zorunlu — girmeden sohbet başlamaz",
    contactAskHint: {
      off: "Ziyaretçi hiçbir şey yazmadan sohbete başlar. Yazıp çıkarsa ona dönecek bir adresiniz olmaz.",
      optional: "İlk mesajından sonra sohbetin içinde e-posta adresi sorulur; “Şimdi değil” diyerek geçebilir.",
      required: "İlk mesajdan önce ad ve e-posta formu çıkar. Daha az kişi sohbet başlatır, ama herkese dönebilirsiniz.",
    },
    contactAskNote: "Sitenize giriş yapmış ve kimliği doğrulanmış üyelere hiçbir seçenekte sorulmaz.",
    saved: "Kaydedildi. Ziyaretçiler değişikliği sayfayı yenileyince görür.",
    faqTitle: "Sık sorulanlar",
    faqIntro: (max: number) => `Sohbet boşken karşılamanın altında açılır-kapanır sorular olarak görünür (en fazla ${max}). Çoğu kişi cevabını burada bulur.`,
    faqNone: "Henüz soru yok.",
    faqQuestion: "Soru",
    faqAnswer: "Cevap",
    faqAdd: "Soruyu ekle",
    presetTitle: "Hazır tema",
    presetIntro:
      "Widget'ın kabuğunu seçin: başlık, balonlar, açma düğmesi, köşeler, yazı ve açılış hareketi. Renkleriniz, dil, metinler ve açık/koyu ayarı her temada geçerli.",
    applyColours: "Tema seçince önerdiği renkleri de uygula",
    applySuggested: (name: string) => `${name} temasının önerdiği renkleri uygula`,
    presets: {
      classic: { name: "Klasik", desc: "Bugünkü görünüm: renkli başlık, dolgulu balonlar, yuvarlak düğme." },
      paper: { name: "Kâğıt", desc: "Gölgesiz, ince çizgili ve sakin; alt çizgili yazı alanı." },
      neon: { name: "Neon", desc: "Parlayan kenarlar, pahlı köşeler, ızgara zemin; oyun ve teknoloji siteleri için." },
      glass: { name: "Cam", desc: "Buzlu, yarı saydam panel; başlık sohbetin içine yumuşakça karışır." },
      bubble: { name: "Balon", desc: "Yuvarlak yazı, hap balonlar, yaylanarak açılır." },
      terminal: { name: "Terminal", desc: "Mono yazı, balon yok; mesajlar komut satırı günlüğü gibi akar." },
      corporate: { name: "Kurumsal", desc: "İki katlı renkli başlık bandı, sıkı düzen, kare düğme." },
      editorial: { name: "Editoryal", desc: "Serif başlık, krem zemin, balon yerine çizgiler; röportaj düzeni." },
      brutal: { name: "Brütal", desc: "Kalın siyah kenarlar, sert gölgeler, basıldıkça çöken hap düğme." },
      dock: { name: "Yan panel", desc: "Ekranın kenarına tam boy yaslanan panel, kenarda dikey sekme." },
      forge: { name: "Çelik", desc: "Fırçalanmış çelik başlık, buz mavisi kenar ışığı, pahlı köşeler; koyu oyun siteleri için." },
      aurora: { name: "Aurora", desc: "AgentlyDesk'in kendi teması: lacivert ışık geçişli başlık, tek marigold vurgu, konuşma balonu köşeleri." },
    },
    fineTune: "İnce ayar",
    fineTuneIntro: "Seçtiğiniz temanın üstüne küçük değişiklikler. Varsayılanda bırakılanlar temanın kendi değerini kullanır.",
    radius: "Köşe yuvarlaklığı",
    radiusDefault: "Tema varsayılanı",
    font: "Yazı tipi",
    fontPreset: "Tema varsayılanı",
    fontSystem: "Sistem (sade)",
    fontRounded: "Yuvarlak",
    fontSerif: "Tırnaklı (serif)",
    fontMono: "Eş aralıklı (mono)",
    fontHint: "Ziyaretçinin cihazındaki yazı tipleri kullanılır; dışarıdan font yüklenmez.",
    launcher: "Açma düğmesi",
    launcherPreset: "Tema varsayılanı",
    launcherCircle: "Yuvarlak",
    launcherSquare: "Kare",
    launcherPill: "Yazılı (hap)",
    launcherText: "Düğme yazısı",
    launcherTextHint: "Boş bırakılırsa widget Türkçeyse “Sohbet”, İngilizceyse “Chat” yazar.",
  },
  canned: {
    listTitle: "Kayıtlı cevaplar",
    listIntro: "Yanıt kutusunda “/” yazınca açılır; en çok kullanılanlar en üstte görünür.",
    none: "Henüz hazır cevap yok.",
    newTitle: "Yeni hazır cevap",
    shortcut: "Kısayol",
    shortcutHint: (s: string) => `yazarken “/${s || "kisayol"}”`,
    title: "Başlık",
    body: "Metin",
    scope: "Nerede kullanılır",
    allSites: "Tüm siteler",
    onlySite: (name: string) => `Yalnızca ${name}`,
    used: (n: number) => (n > 0 ? `${n} kez kullanıldı` : "Henüz kullanılmadı"),
    saved: (s: string) => `/${s} kaydedildi.`,
    added: (s: string) => `/${s} eklendi.`,
    deleted: (s: string) => `/${s} silindi.`,
    confirmDelete: (s: string) => `/${s} silinsin mi?`,
  },
  triggers: {
    listTitle: "Bu sitedeki kurallar",
    listIntro: "Sohbet kutusu, ziyaretçi yazmadan önce bu mesajı kendiliğinden gösterir.",
    none: "Bu sitede otomatik mesaj yok.",
    newTitle: "Yeni otomatik mesaj",
    name: "Ad",
    nameHint: "yalnızca ekip görür",
    when: "Ne zaman",
    pageLoad: "Sayfa açılınca",
    timeOnPage: "Sayfada belli bir süre kalınca",
    exitIntent: "Sayfadan çıkmak üzereyken",
    delay: "Bekleme (saniye)",
    pages: "Hangi sayfalarda",
    pagesHint: "adreste geçen bir parça, boşsa hepsi",
    pagesExample: "örn. /setup",
    message: "Mesaj",
    onlyOnline: "Yalnızca ekip çevrimiçiyken",
    onlyOnlineHint: "Mesai dışında hızlı cevap sözü veren bir mesaj göstermemek için açık bırakın.",
    describe: (where: string | null, when: string, online: boolean) =>
      `Ziyaretçi ${where ? `adresinde “${where}” geçen sayfalarda` : "tüm sayfalarda"} ${when}${online ? ", yalnızca ekip çevrimiçiyken" : ""}.`,
    whenTime: (s: number) => `${s} saniye kalınca`,
    whenExit: "çıkmak üzereyken",
    whenLoad: (s: number) => (s > 0 ? `açıldıktan ${s} saniye sonra` : "açılınca"),
    toggle: (name: string, on: boolean) => `${name} ${on ? "açık" : "kapalı"}`,
    saved: (name: string) => `“${name}” kaydedildi.`,
    added: (name: string) => `“${name}” eklendi.`,
    deleted: (name: string) => `“${name}” silindi.`,
    confirmDelete: (name: string) => `“${name}” silinsin mi?`,
  },
  hours: {
    weekTitle: "Haftalık saatler",
    weekHint: "İşaretli günlerde, yazan saatler arasında çevrimiçi görünür. Bir güne birden fazla aralık ekleyebilirsiniz.",
    addHoliday: "Tatil günü ekle",
    addHolidayHint: "O gün saatlerden bağımsız olarak kapalı görünür.",
    holidayList: "Eklenen günler",
    title: "Çalışma saatleri",
    alwaysOpen: "Her zaman açık",
    alwaysOpenHint: "Açıkken sohbet kutusu her saat “çevrimiçi” görünür.",
    timezone: "Saat dilimi",
    days: ["Pazartesi", "Salı", "Çarşamba", "Perşembe", "Cuma", "Cumartesi", "Pazar"] as string[],
    closed: "Kapalı",
    from: (day: string) => `${day} başlangıç`,
    to: (day: string) => `${day} bitiş`,
    removeRange: (day: string) => `${day} aralığını sil`,
    addRange: "Aralık ekle",
    fillIn: (day: string) => `${day}: saatleri doldurun.`,
    order: (day: string) => `${day}: başlangıç saati bitişten önce olmalı.`,
    holidays: "Tatil günleri",
    holidaysIntro: "Bu tarihlerde saatlerden bağımsız olarak kapalı görünür.",
    noHolidays: "Tatil günü yok.",
    date: "Tarih",
    removeDate: (d: string) => `${d} sil`,
    whenClosed: "Kapalıyken",
    offlineMessage: "Ziyaretçiye gösterilecek mesaj",
    offlineEmail: "Bırakılan mesajların gideceği e-posta",
    saved: "Mesai saatleri kaydedildi.",
  },
  team: {
    workspaceNameHint: "Panelin üstünde ve e-postalarda görünür; müşteriler görmez.",
    autoRow: "Dağıtım şekli",
    members: "Ekip üyeleri",
    you: "(siz)",
    twoFactorOn: "2FA açık",
    twoFactorOff: "2FA kurulmadı",
    inactive: "Pasif",
    sitesVisible: "Görebildiği siteler",
    allSites: "Tüm siteleri görür.",
    roleOf: (name: string) => `${name} rolü`,
    deactivate: "Pasifleştir",
    activate: "Aktifleştir",
    deactivated: (name: string) => `${name} pasifleştirildi.`,
    activated: (name: string) => `${name} yeniden aktif.`,
    roleChanged: (name: string) => `${name}: rol değişti.`,
    sitesChanged: (name: string) => `${name}: siteler güncellendi.`,
    addTitle: "Operatör ekle",
    addIntro: "Kişi ilk girişinde iki adımlı doğrulamayı kurar.",
    name: "Ad",
    email: "E-posta",
    tempPassword: "Geçici şifre",
    role: "Rol",
    roleAgent: "Operatör (yalnızca seçilen siteler)",
    roleAdmin: "Yönetici (tüm siteler ve ayarlar)",
    sitesToSee: "Görebileceği siteler",
    add: "Operatörü ekle",
    added: (name: string) => `${name} eklendi. Geçici şifreyi kendisine iletin; ilk girişte iki adımlı doğrulamayı kuracak.`,
    autoTitle: "Yeni sohbetlerin dağıtımı",
    autoIntro: "Yalnızca durumu “çevrimiçi” olan ve paneli açık olan operatörler sohbet alır. Kimse yoksa sohbet kuyrukta bekler.",
    autoOff: "Kendiliğinden atanmasın, kuyrukta beklesin",
    autoBalanced: "Çevrimiçi operatörlere dağıt (en az açık sohbeti olana)",
    autoSaved: "Dağıtım ayarı kaydedildi.",
    workspaceTitle: "Çalışma alanı",
    workspaceName: "Çalışma alanı adı",
    workspaceSaved: "Çalışma alanı adı kaydedildi.",
    inviteByMail: "Şifresini kendisi belirlesin",
    inviteByMailHint: "Kişiye e-postayla şifre belirleme bağlantısı gönderilir. Kapatırsanız geçici şifreyi siz yazıp kendisine iletirsiniz.",
    invited: (name: string, email: string) => `${name} eklendi, davet ${email} adresine gönderildi.`,
    inviteMailFailed: (name: string) =>
      `${name} eklendi ama davet e-postası gönderilemedi. Satırındaki “Şifre sıfırla” ile bağlantıyı yeniden gönderebilirsiniz.`,
    editMember: "Düzenle",
    editTitle: (name: string) => `${name}: hesap bilgileri`,
    editBody: "E-posta değişirse kişinin açık oturumları kapanır ve yeni adresle giriş yapması gerekir.",
    memberSaved: (name: string) => `${name} güncellendi.`,
    resetPassword: "Şifre sıfırla",
    resetTitle: (name: string) => `${name}: şifre sıfırlama`,
    resetBody: "Kişiye yeni şifre belirleme bağlantısı gönderebilir ya da geçici bir şifre yazabilirsiniz. İki durumda da iki adımlı doğrulaması olduğu gibi kalır.",
    resetSendLink: "E-postayla bağlantı gönder",
    resetLinkSent: (email: string) => `Şifre belirleme bağlantısı ${email} adresine gönderildi.`,
    resetSetTemp: "Geçici şifre yaz",
    resetTempTitle: (name: string) => `${name}: geçici şifre`,
    resetTempSaved: (name: string) => `${name} için geçici şifre kaydedildi, açık oturumları kapatıldı.`,
    reset2fa: "2FA sıfırla",
    reset2faTitle: (name: string) => `${name}: iki adımlı doğrulama sıfırlansın mı?`,
    reset2faBody: "Telefonunu ve kurtarma kodlarını kaybeden kişi için. Tüm oturumları kapanır ve bir sonraki girişinde doğrulamayı yeniden kurar.",
    reset2faDone: (name: string) => `${name}: iki adımlı doğrulama sıfırlandı, ilk girişinde yeniden kuracak.`,
    deleteMember: "Sil",
    deleteMemberTitle: (name: string) => `${name} silinsin mi?`,
    deleteMemberBody: "Hesap kalıcı olarak silinir. Yazdığı mesajlar sohbetlerde kalır, üzerindeki açık sohbetler atanmamış olur. Yalnızca geçici olarak kapatmak için “Pasifleştir” yeterlidir.",
    memberDeleted: (name: string) => `${name} silindi.`,
    moreActions: (name: string) => `${name} için diğer işlemler`,
  },
  history: {
    title: "Geçmiş",
    intro: "Çözülmüş ve kapanmış sohbetler. Mesajların içinde de arar.",
    search: "Mesajlarda, ad veya e-postada ara",
    from: "Başlangıç",
    to: "Bitiş",
    operator: "Operatör",
    anyone: "Herkes",
    rating: "Puan",
    anyRating: "Hepsi",
    stars: (n: number) => `${n} yıldız`,
    noRating: "Puan verilmemiş",
    empty: "Bu süzgeçlerle sohbet bulunamadı.",
    more: "Daha fazla göster",
    closedAt: (d: string) => `Kapanış: ${d}`,
    tagPlaceholder: "örn. iade",
    exported: (n: number) => `${n} sohbet dışa aktarıldı.`,
    exportCapped: (n: number) => `İlk ${n} sohbet dışa aktarıldı. Daha fazlası için tarih aralığını daraltın.`,
    csv: {
      id: "No",
      site: "Site",
      visitor: "Ziyaretçi",
      email: "E-posta",
      status: "Durum",
      operator: "Operatör",
      rating: "Puan",
      tags: "Etiketler",
      started: "Başlangıç",
      closed: "Kapanış",
    },
  },
  stats: {
    title: "İstatistik",
    range: "Aralık",
    today: "Bugün",
    week: "Son 7 gün",
    month: "Son 30 gün",
    conversations: "Sohbet",
    unanswered: "Cevapsız kalan",
    firstResponse: "İlk cevap (ortalama)",
    firstResponseMedian: (x: string) => `medyan ${x}`,
    resolution: "Çözüm süresi (ortalama)",
    rating: "Ortalama puan",
    ratingCount: (n: number) => `${n} değerlendirme`,
    perDay: "Günlere göre sohbet",
    busiest: "En yoğun saatler",
    busiestHint: "Koyu kareler, o gün ve saatte başlayan sohbetin çok olduğunu gösterir.",
    team: "Ekip",
    operator: "Operatör",
    handled: "Üstlendiği",
    avgFirst: "İlk cevap",
    avgRating: "Puan",
    topics: "Konular",
    topicsHint: "Hızlı yanıt butonlarından ve etiketlerden.",
    comments: "Son yorumlar",
    noData: "Bu aralıkta veri yok.",
    none: "—",
    weekdays: ["Pzt", "Sal", "Çar", "Per", "Cum", "Cmt", "Paz"] as string[],
    tableView: "Tablo olarak göster",
    date: "Tarih",
    count: "Sayı",
    seconds: (n: number) => `${n} sn`,
    minutes: (n: number) => `${n} dk`,
    hoursMinutes: (h: number, m: number) => `${h} sa ${m} dk`,
    distribution: "Puan dağılımı",
    custom: "Özel aralık",
    from: "Başlangıç",
    to: "Bitiş",
    customInvalid: "Bitiş tarihi başlangıçtan önce olamaz ve aralık en fazla 366 gün olabilir.",
    csvMetric: "Ölçü",
    csvValue: "Değer",
  },
  spam: {
    rulesTitle: "Mesaj kuralları",
    wordsTitle: "Yasaklı kelimeler",
    wordsIntro: "Bu kelime veya ifadeleri içeren ziyaretçi mesajı gönderilmez; ziyaretçiye nedeni kendi dilinde söylenir. Liste yalnızca sunucuda durur, ziyaretçinin tarayıcısına gitmez.",
    wordsLabel: "Kelimeler",
    wordsHint: "her satıra bir kelime veya ifade; büyük-küçük harf fark etmez, tam kelime eşleşir",
    links: "Bağlantıları engelle",
    linksHint: "Ziyaretçi mesajlarında web adresi (örn. site.com) olmasına izin verilmez.",
    repeats: "Tekrar sınırı",
    repeatsHint: "Aynı mesaj bir dakika içinde bu kadar kez gönderildiyse sonrakiler reddedilir. 0 kapatır.",
    saved: "Spam ayarları kaydedildi.",
    bansTitle: "Engelli ziyaretçiler",
    bansIntro: "Engellenen kişiler sohbet kutusunu açamaz. Yanlışlıkla engellenen birini buradan geri açabilirsiniz.",
    bansNone: "Engelli ziyaretçi yok.",
    banReason: (reason: string) => `Neden: ${reason}`,
    bannedAt: (date: string) => `${date} tarihinde engellendi`,
    bansFailed: "Engelli listesi alınamadı.",
  },
  audit: {
    intro: "Ayarlarda ve hesaplarda kimin neyi, ne zaman yaptığı. Mesaj içerikleri burada tutulmaz.",
    filter: "Eylem",
    all: "Tümü",
    when: "Zaman",
    who: "Kim",
    what: "Ne yaptı",
    system: "Sistem",
    /** Bir başka çalışma alanındaki platform yöneticisinin yazdığı satır: kimliği gösterilmez. */
    platform: "AgentlyDesk",
    empty: "Bu süzgeçle kayıt yok.",
    more: "Daha eski kayıtlar",
    actor: "Kişi",
    targetColumn: "Kime / neye",
    anyone: "Herkes",
    from: "Başlangıç",
    to: "Bitiş",
    exported: (n: number) => `${n} kayıt dışa aktarıldı.`,
    exportCapped: (n: number) => `İlk ${n} kayıt dışa aktarıldı. Daha fazlası için tarih aralığını daraltın.`,
    actions: {
      "operator.login": "Giriş yaptı",
      "operator.login_dev_no_2fa": "Geliştirme girişi (2FA'sız)",
      "operator.login_failed": "Başarısız giriş denemesi",
      "operator.logout": "Çıkış yaptı",
      "operator.2fa_enabled": "İki adımlı doğrulamayı kurdu",
      "operator.2fa_recovery_used": "Kurtarma koduyla girdi",
      "operator.2fa_failed": "Hatalı iki adımlı doğrulama kodu",
      "operator.password_changed": "Şifresini değiştirdi",
      "operator.recovery_codes_regenerated": "Kurtarma kodlarını yeniledi",
      "operator.created": "Operatör ekledi",
      "operator.updated": "Operatörü güncelledi",
      "operator.deactivated": "Operatörü pasifleştirdi",
      "operator.sites_changed": "Operatörün sitelerini değiştirdi",
      "site.created": "Site oluşturdu",
      "site.updated": "Site ayarlarını değiştirdi",
      "site.rotate_key": "Kimlik anahtarını yeniledi",
      "site.revoke_previous_key": "Eski kimlik anahtarını iptal etti",
      "site.rotate_webhook_secret": "Webhook anahtarını yeniledi",
      "site.revoke_previous_webhook_secret": "Eski webhook anahtarını iptal etti",
      "site.origins_changed": "İzinli alan adlarını değiştirdi",
      "visitor.banned": "Ziyaretçi engelledi",
      "visitor.unbanned": "Engeli kaldırdı",
      "visitor.purged": "Ziyaretçinin tüm verilerini sildi",
      "conversation.deleted": "Sohbet sildi",
      "conversation.ticket_created": "Sohbetten destek talebi açtı",
      "workspace.updated": "Ekip ayarlarını değiştirdi",
      "workspace.created": "Çalışma alanı açıldı",
      "workspace.plan_changed": "Plan değişti",
      "workspace.deleted": "Çalışma alanı sildi",
      "operator.invite_sent": "Davet gönderildi",
      "telegram.group_linked": "Telegram grubu bağlandı",
      "telegram.group_unlinked": "Telegram grubu kaldırıldı",
      "telegram.operator_linked": "Telegram hesabı bağlandı",
      "telegram.operator_unlinked": "Telegram hesabı kaldırıldı",
      "operator.password_reset_requested": "Şifre sıfırlama istedi",
      "operator.password_reset_sent": "Şifre sıfırlama bağlantısı gönderdi",
      "operator.2fa_reset": "İki adımlı doğrulamayı sıfırladı",
      "operator.deleted": "Operatörü sildi",
      "site.deleted": "Siteyi sildi",
      "visitor.updated": "Ziyaretçi bilgilerini düzeltti",
    } as Record<string, string>,
    /** Adı sunucu, kaydı yazarken saklar; eski satırlarda ve silinmiş ziyaretçilerde ad yoktur. */
    target: (type: string, id: string, name?: string | null) => {
      const kind =
        type === "site"
          ? "site"
          : type === "operator"
            ? "operatör"
            : type === "visitor"
              ? "ziyaretçi"
              : type === "conversation"
                ? "sohbet"
                : type === "workspace"
                  ? "çalışma alanı"
                  : type;
      return name ? `${kind}: ${name} (#${id})` : `${kind} #${id}`;
    },
  },
  earlyAccess: {
    title: "Erken erişim",
    intro: "AgentlyDesk'in tanıtım sitesinden bırakılan istekler, en yeniden eskiye.",
    filterLabel: "Duruma göre süzgeç",
    statusAll: "Tümü",
    statuses: {
      new: "Yeni",
      contacted: "İletişime geçildi",
      invited: "Davet edildi",
      rejected: "Reddedildi",
    } as Record<string, string>,
    date: "Tarih",
    name: "Ad",
    email: "E-posta",
    company: "Şirket",
    website: "Web sitesi",
    teamSize: "Ekip büyüklüğü",
    plan: "Plan",
    message: "Mesaj",
    status: "Durum",
    none: "—",
    showMessage: "Mesajı göster",
    hideMessage: "Mesajı gizle",
    empty: "Bu süzgeçle istek yok.",
    loadFailed: "İstekler alınamadı.",
    updateFailed: "Durum güncellenemedi.",
    approve: "Onayla",
    approveTitle: (name: string) => `${name} için hesap açılsın mı?`,
    approveIntro: "Çalışma alanı ve sahip hesabı oluşturulur, şifre belirleme bağlantısı e-posta ile gönderilir.",
    approvePlan: "Başlangıç planı",
    approveSubmit: "Hesabı aç ve daveti gönder",
    approving: "Açılıyor…",
    approved: (email: string) => `Hesap açıldı, davet ${email} adresine gönderildi.`,
    toCustomers: "Müşteriler ekranına git",
    resend: "Daveti yeniden gönder",
    resent: (email: string) => `Davet ${email} adresine yeniden gönderildi.`,
    errSmtp: "SMTP ayarlı değil, davet e-postası gönderilemez.",
    errAlreadyOperator: "Bu e-posta adresi zaten bir hesaba ait.",
    errNotApprovable: "Bu istek artık onaylanabilir durumda değil.",
    errSlugConflict: "Bu ada uygun bir adres üretilemedi. Şirket adını değiştirip tekrar deneyin.",
    errMailFailed: "Hesap açıldı ama davet e-postası gönderilemedi. Daveti yeniden gönderin.",
    approveFailed: "Hesap açılamadı.",
  },
  plan: {
    names: { free: "Free", starter: "Starter", team: "Team", unlimited: "Sınırsız" } as Record<string, string>,
    limitTitle: "Plan sınırı",
    limits: {
      sites: "Planınızın site sınırına ulaştınız.",
      operators: "Planınızın operatör sınırına ulaştınız.",
      themes: "Hazır temalar ve ince ayar planınızda yok.",
      cannedReplies: "Hazır cevaplar planınızda yok.",
      verifiedIdentity: "Doğrulanmış kimlik planınızda yok.",
      ticketWebhook: "Talep webhook'u planınızda yok.",
      auditLog: "Denetim kaydı planınızda yok.",
    } as Record<string, string>,
    upgrade: "Planı yükselt",
    askOwner: "Yükseltmek için çalışma alanı sahibine haber verin.",
    needsPlan: (name: string) => `${name} planında`,
    suspendedTitle: "Çalışma alanı askıya alındı",
    suspendedBody: "Bu çalışma alanı şu an kullanılamıyor; sohbet kutusu da ziyaretçilere kapalı.",
    suspendedOwner: "Ödemenizi tamamlayarak ya da bizimle iletişime geçerek devam edebilirsiniz.",
    suspendedOther: "Devam etmek için çalışma alanı sahibiyle konuşun.",
    goBilling: "Faturalandırmaya git",
  },
  billing: {
    intro: "Planınız, kullanımınız ve ödemeleriniz.",
    loadFailed: "Faturalandırma bilgisi alınamadı.",
    notConfiguredTitle: "Ödeme henüz açık değil",
    notConfiguredBody: "Kart ödemeleri bu kurulumda kapalı. Plan değişikliği için bizimle iletişime geçin.",
    planTitle: "Planınız",
    statusActive: "etkin",
    statusPastDue: "ödeme gecikti",
    statusCanceled: "iptal edildi",
    statusSuspended: "askıya alındı",
    graceNote: (stored: string, until: string) => `${stored} — ödeme gecikti, ek süre ${until} tarihinde bitiyor.`,
    downgradedNote: (stored: string) => `${stored} aboneliğiniz için ödeme alınamadı, şu an Free kuralları geçerli.`,
    suspendedNote: "Çalışma alanı askıya alındı, şu an Free kuralları geçerli.",
    renews: (date: string) => `Sonraki yenileme: ${date}`,
    perMonth: (price: string) => `${price} / ay`,
    usageTitle: "Kullanım",
    sites: "Siteler",
    operators: "Operatörler",
    ofLimit: (used: number, limit: string) => `${used} / ${limit}`,
    upgradeTitle: "Plan değiştir",
    upgradeIntro: "Fiyat çalışma alanı başınadır; operatör başına ücret yoktur.",
    upgradeStarter: "Starter'a geç",
    upgradeTeam: "Team'e geç",
    manageTitle: "Ödeme ve faturalar",
    manage: "Ödeme sayfasını aç",
    manageHint: "Kart, fatura ve plan değişikliği Stripe'ın kendi sayfasında yapılır.",
    checkoutSuccess: "Ödemeniz alındı. Planınızın güncellenmesi bir dakikayı bulabilir.",
    checkoutCancel: "Ödeme tamamlanmadı, planınız değişmedi.",
    refresh: "Yenile",
    errSubscriptionExists: "Zaten bir aboneliğiniz var. Plan değişikliğini ödeme sayfasından yapın.",
    errNoCustomer: "Henüz bir ödeme kaydınız yok. Önce bir plan seçin.",
    errNoSubscription: "Etkin bir abonelik yok. Önce bir plana geçin.",
    errNotConfigured: "Ödeme sağlayıcısı bu kurulumda ayarlı değil.",
    errProvider: "Ödeme sağlayıcısına ulaşılamadı. Biraz sonra tekrar deneyin.",
  },
  customers: {
    title: "Müşteriler",
    intro: "Tüm çalışma alanları, planları ve sahipleri. En yeniden eskiye.",
    loadFailed: "Çalışma alanları alınamadı.",
    empty: "Henüz çalışma alanı yok.",
    name: "Çalışma alanı",
    owner: "Sahip",
    plan: "Plan",
    usage: "Kullanım",
    created: "Oluşturuldu",
    actions: "İşlemler",
    noOwner: "—",
    usageCell: (sites: number, operators: number) => `${sites} site · ${operators} operatör`,
    save: "Kaydet",
    suspend: "Askıya al",
    unsuspend: "Askıyı kaldır",
    confirmSuspend: (name: string) => `${name} askıya alınsın mı? Sohbet kutusu ve panel kapanır.`,
    stripeWarning: "Stripe aboneliği var — bir sonraki Stripe olayı bu değişikliği geri alabilir.",
    saved: (name: string) => `${name} güncellendi.`,
    pausedNote: (sites: number, operators: number) => `${sites} site ve ${operators} operatör durduruldu.`,
    resumedNote: (sites: number, operators: number) => `${sites} site ve ${operators} operatör yeniden açıldı.`,
    updateFailed: "Güncellenemedi.",
    resend: "Daveti yeniden gönder",
    resent: (email: string) => `Davet ${email} adresine gönderildi.`,
    errSmtp: "SMTP ayarlı değil, davet e-postası gönderilemez.",
    errNoOwner: "Bu çalışma alanının etkin bir sahibi yok.",
    errAlreadyActive: "Sahip hesabı zaten kurulmuş; davet yeniden gönderilemez.",
    errMailFailed: "E-posta gönderilemedi.",
    search: "Çalışma alanı, adres ya da e-posta ara",
    shown: (shown: number, total: number) => `${total} çalışma alanından ${shown} tanesi gösteriliyor.`,
    noMatch: "Aramayla eşleşen çalışma alanı yok.",
    newTitle: "Yeni müşteri",
    newIntro: "Çalışma alanı ve sahip hesabı açılır, sahibe şifre belirleme bağlantısı e-postayla gönderilir.",
    newOpen: "Yeni müşteri aç",
    workspaceName: "Çalışma alanı adı",
    ownerName: "Sahibin adı",
    ownerEmail: "Sahibin e-postası",
    create: "Hesabı aç ve daveti gönder",
    creating: "Açılıyor…",
    createdOk: (email: string) => `Hesap açıldı, davet ${email} adresine gönderildi.`,
    createdMailFailed: "Hesap açıldı ama davet e-postası gönderilemedi. Listeden “Daveti yeniden gönder”i kullanın.",
    errAlreadyOperator: "Bu e-posta adresi zaten bir hesaba ait.",
    delete: "Sil",
    deleteTitle: (name: string) => `${name} silinsin mi?`,
    deleteBody: "Çalışma alanı; siteleri, operatörleri, tüm sohbetleri ve dosyalarıyla birlikte kalıcı olarak silinir. Geri alınamaz.",
    deleted: (name: string) => `${name} silindi.`,
    errOwnWorkspace: "Kendi çalışma alanınızı silemezsiniz.",
    errHasSubscription: "Etkin bir Stripe aboneliği var. Önce aboneliği iptal edin.",
  },
  setPassword: {
    title: "Şifrenizi belirleyin",
    intro: "AgentlyDesk hesabınız hazır. Bir şifre belirleyin, sonra normal şekilde giriş yapın.",
    password: "Yeni şifre",
    confirm: "Şifre tekrar",
    rule: "En az 12, en fazla 200 karakter.",
    mismatch: "İki şifre aynı değil.",
    submit: "Şifreyi kaydet",
    saving: "Kaydediliyor…",
    doneTitle: "Şifreniz kaydedildi",
    doneBody: "Artık giriş yapabilirsiniz. İlk girişte iki adımlı doğrulamayı kuracaksınız.",
    goLogin: "Giriş ekranına git",
    missingToken: "Bu bağlantı eksik görünüyor. E-postadaki bağlantıyı olduğu gibi açın.",
    weak: "Şifre 12 ile 200 karakter arasında olmalı.",
    invalid: "Bu bağlantı geçersiz veya süresi dolmuş. Yeni bir davet isteyin.",
    failed: "Şifre kaydedilemedi.",
  },
  /**
   * What a refused call says, by the server's `code` (chat-server
   * lib/errors.ts). The server answers in Turkish only, so this is where an
   * English operator's console stops showing Turkish. An unknown code falls
   * back to the server's own sentence — an older console in front of a newer
   * server still says something true, just not translated.
   */
  errors: {
    not_found: "Bulunamadı.",
    invalid_request: "İstek geçerli değil.",
    invalid_query: "Sorgu geçerli değil.",
    invalid_input: "Girilen bilgilerden biri geçerli değil.",
    unauthorized: "Oturumunuz yok ya da süresi doldu. Tekrar giriş yapın.",
    forbidden: "Bunu yapma yetkiniz yok.",
    reauth_required: "Devam etmek için ikinci adımı yeniden doğrulayın.",
    twofa_required: "Paneli kullanmadan önce iki adımlı doğrulamayı kurun.",
    rate_limited: "Çok fazla istek gönderildi. Biraz bekleyip tekrar deneyin.",
    workspace_suspended: "Bu çalışma alanı askıya alındı.",
    plan_limit: "Mevcut planınız buna izin vermiyor.",
    internal_error: "Sunucuda beklenmeyen bir hata oldu.",
    invalid_json: "İstek okunamadı.",
    body_too_large: "Gönderilen veri çok büyük.",
    invalid_credentials: "E-posta veya şifre hatalı.",
    account_locked: (seconds: number) => `Hesap geçici olarak kilitli. ${seconds} saniye sonra tekrar deneyin.`,
    ticket_expired: "Giriş isteğinin süresi doldu. Baştan giriş yapın.",
    ticket_invalid: "Giriş isteği geçerli değil.",
    code_invalid: "Kod doğrulanamadı.",
    code_required: "Kodu girin.",
    twofa_unreadable: "İki adımlı doğrulama ayarınız sunucuda okunamadı.",
    twofa_already_set: "İki adımlı doğrulama zaten kurulu.",
    twofa_not_enabled: "Bu hesapta iki adımlı doğrulama kurulu değil.",
    invalid_token: "Bu bağlantı geçersiz veya süresi dolmuş.",
    weak_password: "Şifre 12 ile 200 karakter arasında olmalı.",
    wrong_password: "Mevcut şifre yanlış.",
    owner_role_required: "Yalnızca bir sahip, başka bir sahip oluşturabilir veya atayabilir.",
    owner_only_target: "Sahip hesabını yalnızca bir sahip değiştirebilir.",
    last_owner: "Son sahip hesabı pasifleştirilemez, rolü de düşürülemez.",
    email_taken: "Bu e-posta zaten kayıtlı.",
    invalid_origin: "İzinli alan adı geçerli değil. Örnek: https://example.com — yol ve joker karakter olmaz.",
    webhook_url_invalid: "Talep adresi geçerli bir adres değil.",
    webhook_url_not_https: "Talep adresi https:// ile başlamalı.",
    webhook_url_ip: "Talep adresi bir alan adı olmalı; IP adresi kabul edilmiyor.",
    invalid_widget_config: "Widget ayarlarından biri geçerli değil.",
    noPreviousKey: "Süresi dolmamış eski bir anahtar yok.",
    unknown_timezone: "Bu saat dilimi tanınmıyor.",
    operator_not_on_site: "Bu operatör bu siteye atanmamış.",
    no_visitor_email: "Bu ziyaretçinin kayıtlı e-posta adresi yok.",
    smtp_required: "E-posta gönderimi yapılandırılmamış.",
    message_too_long: "Mesaj çok uzun.",
    message_empty: "Mesaj boş.",
    invalid: "İstek geçerli değil.",
    exists: "Bu sohbetten zaten talep açılmış.",
    inProgress: "Bu sohbet için talep şu anda açılıyor.",
    notConfigured: "Bu site için talep adresi ayarlanmamış.",
    webhookSecretMissing: "Bu site için webhook imza anahtarı yok. Ayarlar → Siteler'den oluşturun.",
    notIdentified: "Ziyaretçi sitede oturum açmamış.",
    webhookFailed: "Site talebi kabul etmedi.",
    attachments_disabled: "Bu sitede dosya gönderimi kapalı.",
    link_expired: "Bağlantının süresi dolmuş.",
    upload_rejected: "Dosya kabul edilmedi.",
    empty_file: "Dosya boş.",
    file_too_large: "Dosya 10 MB'tan büyük olamaz.",
    unsupported_image_type: "Yalnızca PNG, JPG, GIF veya WEBP gönderilebilir.",
    conversation_attachment_limit: "Bu sohbette daha fazla dosya gönderilemez.",
    storage_full: "Bu sitenin dosya alanı dolu.",
    avatar_too_large: "Avatar 2 MB'tan büyük olamaz.",
    invalid_display_name: "Ad 2–40 karakter olmalı.",
    invalid_range: "Bu aralık geçerli değil.",
    billing_not_configured: "Ödeme sistemi yapılandırılmamış.",
    billing_provider_error: "Ödeme sağlayıcısına ulaşılamadı. Lütfen tekrar deneyin.",
    subscription_exists: "Bu çalışma alanının zaten bir aboneliği var; değişiklikleri ödeme portalından yapın.",
    no_customer: "Henüz bir ödeme hesabı yok.",
    already_operator: "Bu e-posta zaten bir operatöre ait.",
    not_approvable: "Bu başvuru onaylanamaz; zaten davet edilmiş ya da reddedilmiş.",
    slug_conflict: "Çalışma alanı adresi çakıştı. Tekrar deneyin.",
    mail_failed: "E-posta gönderilemedi.",
    no_owner: "Bu çalışma alanının etkin bir sahibi yok.",
    already_active: "Sahip hesabı zaten kullanımda.",
    confirm_mismatch: "Onay metni eşleşmiyor.",
    cannot_target_self: "Bu işlem kendi hesabınızda yapılamaz.",
    operator_inactive: "Pasif bir hesaba bağlantı gönderilemez.",
    message_not_editable: "Bu mesaj değiştirilemez.",
    not_message_author: "Yalnızca kendi mesajınızı değiştirebilirsiniz.",
    visitor_verified: "Doğrulanmış üyenin bilgileri siteden gelir, buradan değiştirilemez.",
    visitor_banned: "Bu ziyaretçi engelli.",
    unsupported_file_type: "Yalnızca resim, PDF veya düz metin (.txt, .log) gönderilebilir.",
    cannot_delete_own_workspace: "Kendi çalışma alanınızı silemezsiniz.",
    has_subscription: "Bu çalışma alanının etkin bir Stripe aboneliği var. Önce aboneliği iptal edin.",
  },
};

export type Dict = typeof tr;

/**
 * "1 seats" reads as a bug. English needs the singular; Turkish does not
 * inflect a noun after a number at all, so only this dictionary uses it.
 */
const count = (n: number, one: string, many: string): string => `${n} ${n === 1 ? one : many}`;

const en: Dict = {
  common: {
    loading: "Loading…",
    offline: "Can't reach the server. The console will open by itself once you're back online.",
    retry: "Try again",
    save: "Save",
    saveChanges: "Save changes",
    cancel: "Cancel",
    add: "Add",
    edit: "Edit",
    delete: "Delete",
    close: "Close",
    download: "Download",
    saveFailed: "Couldn't save.",
    actionFailed: "That didn't work.",
    unexpected: "Something unexpected went wrong.",
    optional: "optional",
    site: "Site",
    appTitle: "AgentlyDesk",
    appTitleUnread: (n: number) => `(${n}) AgentlyDesk`,
    readOnly: "Only admins can change these settings. You can view them.",
    createSiteFirst: "Create a site first.",
    sitesFailed: "Couldn't load the sites.",
    confirm: "Confirm",
    typeToConfirm: (text: string) => `Type “${text}” to confirm`,
    connectionLost: "Connection lost — reconnecting. Anything that arrives meanwhile loads once it's back.",
    exportCsv: "Download CSV",
    exporting: "Preparing…",
    exportFailed: "Couldn't export.",
    allSites: "All sites",
    tag: "Tag",
    showMore: "Show more",
  },
  time: {
    now: "just now",
    minutes: (n: number) => `${n}m`,
    hours: (n: number) => `${n}h`,
    days: (n: number) => `${n}d`,
    today: "Today",
    yesterday: "Yesterday",
    ago: (x: string) => `${x} ago`,
  },
  status: {
    pending: "Waiting",
    open: "Open",
    resolved: "Resolved",
    closed: "Closed",
  },
  visitor: (id: number) => `Visitor #${id}`,
  visitors: {
    location: "Location",
    deviceCol: "Device",
    devices: { mobile: "Mobile", tablet: "Tablet", desktop: "Desktop" },
    ip: "IP address",
    perSite: "Visitors per site",
    allSites: "All sites",
    title: "Visitors",
    intro: "People on your sites right now. The list refreshes by itself. Browsing isn't recorded — when someone leaves the page they leave the list.",
    empty: "Nobody is on the site right now.",
    emptyHint: "Someone appears here as soon as a page with the widget on it is open.",
    loadFailed: "Couldn't load the visitors.",
    who: "Visitor",
    page: "Page they're on",
    since: "Arrived",
    unknownPage: "Unknown",
    tabs: (n: number) => count(n, "tab", "tabs"),
    openChat: "Open the chat",
    start: "Start a chat",
    startTitle: (name: string) => `Start a chat with ${name}`,
    startBody: "Your message appears in the widget on the visitor's screen, and the conversation is assigned to you.",
    startLabel: "Your first message",
    startPlaceholder: "Hi! Is there anything I can help you with?",
    startFailed: "Couldn't start the chat.",
    count: (n: number) => count(n, "person", "people"),
  },
  role: { owner: "Owner", admin: "Admin", agent: "Agent" },
  login: {
    title: "AgentlyDesk",
    sub: "Sign in to continue.",
    email: "E-mail",
    password: "Password",
    checking: "Checking…",
    continue: "Continue",
    twoFactorTitle: "Two-step verification",
    recoveryHint: "Enter one of your recovery codes.",
    codeHint: "Enter the six-digit code from your authenticator app.",
    recoveryCode: "Recovery code",
    code: "Code",
    verifying: "Verifying…",
    signIn: "Sign in",
    useApp: "Use the app code instead",
    noPhone: "I can't get to my phone",
    enrolTitle: "Set up two-step verification",
    enrolWhy: "This console shows every customer conversation, so a second step is required.",
    qrFallback: "Can't scan the QR code? Enter the key by hand:",
    appCode: "Code from the app",
    finishSetup: "Finish setup",
    codesTitle: "Recovery codes",
    codesHint: "If you lose your phone, these are the only way back in. Each works once and won't be shown again.",
    codesDone: "I've saved them, continue",
    language: "Language",
    forgot: "Forgot your password?",
    forgotTitle: "Reset your password",
    forgotHint: "Enter your e-mail address and we'll send you a link to choose a new password. Your two-step verification stays as it is.",
    forgotSubmit: "Send the link",
    forgotSending: "Sending…",
    forgotSent: "If that address is registered, a link to choose a new password is on its way. Check your inbox; the link works for 72 hours.",
    backToLogin: "Back to sign-in",
  },
  nav: {
    label: "Main menu",
    inbox: "Inbox",
    inboxWaiting: (n: number) => `Inbox, ${n} conversations waiting for a reply`,
    history: "History",
    visitors: "Visitors",
    stats: "Statistics",
    earlyAccess: "Early access",
    customers: "Customers",
    settings: "Settings",
    themeSystem: "Theme: system setting",
    themeLight: "Theme: light",
    themeDark: "Theme: dark",
    presenceOnline: "Online. Set yourself away",
    presenceAway: "Away. Go online",
    signOut: "Sign out",
  },
  inbox: {
    title: "Inbox",
    listLabel: "Conversations",
    filterLabel: "Conversation filter",
    tabMine: "Mine",
    tabUnassigned: "Unassigned",
    tabActive: "All open",
    tabPending: "Unanswered",
    tabResolved: "Resolved",
    searchPlaceholder: "Search names, e-mails or messages",
    searchLabel: "Search conversations",
    noResultsTitle: "No results",
    noResultsHint: "Try a shorter search or another tab.",
    emptyTitle: "Nobody is waiting here",
    emptyHint: "When a customer writes, you'll hear a sound and they'll appear here.",
    noMessages: "No messages yet",
    sentImage: "Sent an image",
    teamPrefix: (text: string) => `Support: ${text}`,
    waitingTitle: (when: string) => `The customer wrote ${when} and is waiting for a reply`,
    verifiedTitle: "Identity verified by the site",
    assigneeTitle: (name: string) => `Assigned to ${name}`,
    unread: (n: number) => `${n} unread messages`,
    conversationLabel: "Conversation",
    pickTitle: "Pick a conversation",
    pickHint: "Open one from the list on the left. The coloured strip on its edge shows how long the customer has been waiting.",
    typing: (name: string) => `${name} is typing…`,
    listFailed: "Couldn't load the list.",
    openFailed: "Couldn't open the conversation.",
    resolvedToast: "Marked as resolved.",
    reopenedToast: "Conversation reopened.",
    transcriptSent: (email: string) => `Transcript sent to ${email}.`,
    banPrompt: "Reason for the ban (only the team sees this):",
    banDefault: "Not given",
    bannedToast: "Visitor banned.",
    unbannedToast: "Ban lifted.",
    deleteConfirm:
      "This conversation and every message and image in it will be erased. This cannot be undone and there is no backup. Continue?",
    deletedToast: "Conversation permanently deleted.",
    purgeConfirm: (name: string) =>
      `EVERYTHING about ${name} will be erased: their record, all of their conversations, messages, images and their ban entry. This cannot be undone and there is no backup. Continue?`,
    purgedToast: (n: number) => `The visitor and their ${n} conversations were permanently deleted.`,
    draft: (name: string) => `${name} is typing (not sent yet)`,
    assignedBy: (name: string) => `${name} handed you a conversation`,
    assignedAuto: "A new conversation was assigned to you",
    mentionedBy: (name: string) => `${name} mentioned you in a note`,
    handedOver: (name: string) => `Conversation handed over to ${name}.`,
    ticketCreated: (id: string) => `Support ticket opened: #${id}`,
    sentFile: "Sent a file",
    loadMore: "Show more conversations",
    filterTag: "Filter by tag",
    select: "Select",
    selectDone: "Done selecting",
    selected: (n: number) => `${n} selected`,
    selectAll: "Select everything in the list",
    bulkResolve: "Resolve",
    bulkAssignMe: "Assign to me",
    bulkDelete: "Delete",
    bulkDeleteTitle: (n: number) => `Delete ${count(n, "conversation", "conversations")}?`,
    bulkDeleteBody: "The selected conversations are erased with every message and file in them. This cannot be undone and there is no backup.",
    bulkDone: (n: number) => `${count(n, "conversation", "conversations")} updated.`,
    bulkDeleted: (n: number) => `${count(n, "conversation", "conversations")} permanently deleted.`,
    bulkPartial: (ok: number, failed: number) => `Done for ${ok}, but it didn't work for ${failed}.`,
    banTitle: "Ban this visitor",
    banReason: "Reason for the ban",
    banReasonHint: "optional, only the team sees it",
    banConfirm: "Ban",
    deleteTitle: "Delete this conversation?",
    purgeTitle: "Erase all their data?",
    messageDeleted: "Message deleted.",
    visitorSaved: "Visitor details saved.",
    ticketErrors: {
      notConfigured: "No ticket address is set for this site (Settings → Sites).",
      notIdentified: "The visitor isn't signed in on the site, so the ticket can't be tied to an account.",
      exists: "A ticket was already opened from this conversation.",
      inProgress: "A ticket is being opened from this chat right now, please wait.",
      webhookFailed: "The site didn't accept the ticket. Check the address and the key.",
      webhookSecretMissing: "This site has no webhook key yet. An admin needs to create one in Settings → Sites.",
    } as Record<string, string>,
  },
  conv: {
    back: "Back to the list",
    removeTag: (tag: string) => `Remove the ${tag} tag`,
    removeTagTitle: "Remove tag",
    assignedChange: (name: string) => `Assigned to ${name}. Change`,
    assignSomeone: "Assign to someone",
    mine: "Mine",
    assign: "Assign",
    assignMe: "Assign to me",
    unassign: "Unassign",
    tagAdd: "Add a tag",
    tag: "Tag",
    tagPlaceholder: "Type a tag and press Enter",
    reopen: "Reopen",
    resolve: "Resolve",
    more: "More actions",
    transcript: "E-mail the transcript",
    noEmail: "This visitor has no e-mail address on file",
    info: "Customer details",
    readOnly: "This conversation is closed. Reopen it to write.",
    openTicket: "Open a support ticket",
    ticketNoWebhook: "No ticket address is set for this site",
    ticketNoIdentity: "The visitor isn't signed in on the site",
    ticketChip: (id: string) => `Ticket #${id}`,
    handoverTitle: (name: string) => `Hand over to ${name}`,
    handoverNote: "Hand-over note",
    handoverHint: "optional, added to the thread as an internal note and sent to them",
    handoverPlaceholder: "e.g. Wants a refund, order 123",
    handoverConfirm: "Hand over",
    delete: "Delete this conversation for good",
    deleteAdminOnly: "Only admins can delete a conversation",
    loadEarlier: "Load earlier messages",
    downloadTranscript: "Download the transcript (.txt)",
    editMessageTitle: "Edit message",
    editMessageHint: "The customer will see that it was edited.",
    deleteMessageTitle: "Delete this message?",
    deleteMessageBody: "It disappears from the customer's screen too. This cannot be undone.",
    deleteNoteBody: "The internal note is deleted for good.",
  },
  thread: {
    label: "Messages",
    automatic: "Automatic message",
    team: "Support",
    note: "Internal note",
    sending: "sending…",
    uploading: "Uploading",
    edited: "edited",
    editMessage: "Edit message",
    deleteMessage: "Delete message",
  },
  composer: {
    modes: "Message type",
    reply: "Reply",
    note: "Internal note",
    attach: "Attach a file (image, PDF, text)",
    replyPlaceholder: "Write your reply",
    notePlaceholder: "A note only the team will see",
    send: "Send",
    saveNote: "Save note",
    hintEnter: "Enter sends, Shift+Enter adds a line",
    hintSlash: "/ saved replies",
    cannedLabel: "Saved replies",
    noCanned: "No saved replies yet. You can add them in Settings.",
    noCannedMatch: "No saved reply matches this shortcut.",
    sendFailed: "Couldn't send the message.",
    imageType: "Only PNG, JPG, GIF or WEBP images can be sent.",
    imageTooBig: (mb: number) => `Images can't be larger than ${mb} MB.`,
    uploadFailed: "Couldn't send the file.",
    fileType: "Only images (PNG, JPG, GIF, WEBP), PDFs and plain text (.txt, .log) can be sent.",
    fileTooBig: (mb: number) => `Files can't be larger than ${mb} MB.`,
    emoji: "Insert an emoji",
    suggestions: "Suggested saved replies",
    mentionLabel: "Mention a teammate",
    noMention: "No teammate matches.",
    hintMention: "@ to call in a teammate",
  },
  context: {
    label: "Customer details",
    copyEmail: "Copy e-mail",
    copied: "Copied",
    verified: "✓ Account verified",
    verifiedTitle: "The site vouched for this person's account with a signature",
    unverified: "Unverified visitor",
    unverifiedTitle: "Not signed in, or the site didn't verify them",
    banned: "Banned",
    siteAccount: "Site account",
    now: "Right now",
    page: "Page",
    unknown: "Unknown",
    referrer: "Came from",
    conversation: "Conversation",
    startedAt: (date: string, relative: string) => `${date} (${relative})`,
    pages: "Pages visited",
    pagesEmpty: "Pages they visit after the chat opens are listed here.",
    device: "Device",
    browser: "Browser",
    os: "System",
    deviceType: "Device",
    country: "Country",
    firstVisit: "First visit",
    lastSeen: "Last seen",
    past: "Earlier conversations",
    firstChat: "This is their first conversation.",
    emptyPreview: "An image or an empty message",
    actions: "Actions",
    ban: "Ban this visitor",
    unban: "Lift the ban",
    purge: "Erase all their data",
    purgeHint: "Their record, every conversation, message and image is erased for good. This cannot be undone.",
    stars: (n: number) => `${n} of 5 stars`,
    ticket: "Support ticket",
    ticketOpened: (date: string) => `Opened ${date}`,
    ticketOpen: "Open ticket",
    editVisitor: "Edit details",
    editVisitorTitle: "Visitor details",
    editVisitorBody: "If the visitor gave their name or e-mail in the chat, you can put it here.",
    visitorName: "Name",
    visitorEmail: "E-mail",
    liveNow: "On the site now",
  },
  settings: {
    title: "Settings",
    sectionsLabel: "Settings sections",
    groupAccount: "Your account",
    groupWidget: "Chat widget",
    groupAdmin: "Administration",
    tabProfile: "Profile",
    tabNotifications: "Notifications",
    tabSecurity: "Security",
    tabWidget: "Look and greeting",
    tabCanned: "Saved replies",
    tabTriggers: "Automatic messages",
    tabHours: "Working hours",
    tabSites: "Sites",
    tabOperators: "Team",
    tabSpam: "Spam protection",
    tabAudit: "Audit log",
    tabBilling: "Plan and billing",
  },
  profile: {
    photo: "Photo",
    displayNameHint: "The name customers see next to your replies. Make it a person's name, not an e-mail address.",
    preferences: "Preferences",
    emailHint: "Used to sign in; never shown to customers. Ask an admin to change it.",
    seenTitle: "What customers see",
    seenIntro: "This name and photo appear next to your replies. Your e-mail address is never shown.",
    uploadPhoto: "Upload a photo",
    removePhoto: "Remove photo",
    photoHint: "PNG, JPG, GIF or WEBP, up to 2 MB",
    displayName: "Display name",
    saveName: "Save name",
    nameSaved: "Your name was saved.",
    photoSaved: "Your photo was updated.",
    photoTooBig: "Photos can't be larger than 2 MB.",
    account: "Account",
    email: "E-mail",
    role: "Role",
    language: "Console language",
    languageHint: "Only changes your own console. The language customers see is in the widget settings.",
  },
  telegram: {
    title: "Telegram",
    intro:
      "Get chats in a Telegram group and answer from there. Each chat becomes its own topic in the group. " +
      "Note: everyone in the group sees every customer conversation, so add only your team. A linked Telegram " +
      "account is as valuable as a console sign-in; turn on two-step verification in Telegram.",
    notConfigured: "Telegram isn't set up on this server.",
    group: "Team group",
    groupNone: "No group linked. Create a group with Topics on, make the bot an admin (with \"Manage topics\"), then get a code and send it in the group.",
    groupLinked: (title: string) => `Linked: ${title || "group"}`,
    groupCode: "Get group code",
    me: "My account",
    meNone: "Your Telegram account isn't linked. Get a code and send it in the group or to the bot privately.",
    meLinked: (name: string) => `Linked: ${name}`,
    meCode: "Link my account",
    unlink: "Unlink",
    codeHint: "Send this in Telegram (valid 15 minutes, single use): ",
    bot: (name: string) => `Bot: @${name}`,
    copy: "Copy",
    failed: "Couldn't do that, please try again.",
  },
  notify: {
    unanswered: "Waiting-customer reminder",
    unansweredHint: "Rings with a sound and a notification when a customer has waited longer than this for an answer. Only for chats assigned to you or to nobody; once per wait.",
    unansweredOff: "Off",
    unansweredMinutes: (n: number) => `${n} ${n === 1 ? "minute" : "minutes"}`,
    unansweredTitle: "A customer is waiting",
    unansweredBody: (name: string, minutes: number) => `${name} has waited ${minutes} ${minutes === 1 ? "minute" : "minutes"} for an answer.`,
    browserTitle: "In this browser",
    testRow: "Test",
    testHint: "Plays the sound and desktop notification you have on, once, so you can see the settings work.",
    desktopApp: "Windows app",
    desktopAppHint: "Opens the console in its own window: starts with Windows, waits in the tray and shows the unread count on the taskbar.",
    desktopAppDownload: "Download (Windows)",
    title: "Notifications",
    intro: "How you hear about a customer writing. These settings are kept in this browser.",
    sound: "Sound",
    soundHint: "A short sound for a new conversation, and for a message in a conversation you don't have open.",
    tabTitle: "Count in the tab title",
    tabTitleHint: "While you're on another tab, the unread count shows in this tab's title.",
    desktop: "Desktop notifications",
    desktopHint: "A notification in the corner of your screen while the console is in the background.",
    desktopAllow: "Ask the browser for permission",
    desktopDenied: "The browser has blocked notifications. You can allow them from the padlock in the address bar.",
    desktopUnsupported: "This browser doesn't support desktop notifications.",
    test: "Send a test notification",
    testTitle: "AgentlyDesk",
    testBody: "Notifications are working.",
    newChat: "New conversation",
    newMessage: (name: string) => `${name} wrote`,
    image: "Sent an image",
    push: "Phone notifications",
    pushHint: "Notifications even with the console closed. This is per device — switch it on again on each one.",
    pushOn: (n: number) => `On — ${n} ${n === 1 ? "device" : "devices"} registered for this account.`,
    pushEnabling: "Asking for permission…",
    pushFailed: "Couldn't switch notifications on. The browser may have refused permission.",
    pushDenied: "The browser has blocked notifications. Allow them from the padlock in the address bar and try again.",
    pushUnsupported: "This browser can't deliver notifications while it's closed. On a phone, add the console to the home screen and open it from there.",
  },
  security: {
    appCodeHint: "Type the 6-digit code from your authenticator app to get new codes.",
    passwordTitle: "Password",
    passwordIntro: "Changing it signs you out everywhere except this browser.",
    current: "Current password",
    next: "New password",
    nextHint: "at least 12 characters",
    again: "New password (again)",
    mismatch: "The two passwords don't match.",
    change: "Change password",
    changed: "Your password was changed. You've been signed out on other devices.",
    codesTitle: "Recovery codes",
    codesIntro: "If you lose your phone, you can sign in with one of these codes. Making new codes cancels all the old ones.",
    codesWarn: "These codes are shown only now. Print them or keep them in your password manager.",
    codesDone: "I've saved them, hide",
    appCode: "6-digit code from your authenticator app",
    newCodes: "Make new codes",
  },
  sites: {
    revealed: (name: string) => `${name}: secret keys`,
    tabGeneral: "General",
    tabInstall: "Installation",
    tabKeys: "Secret keys",
    tabProtection: "Protection",
    tabDanger: "Danger zone",
    installIntro: "What it takes to put the chat on your site. Add the code to every page and list your domain as allowed.",
    keysIntro: "Both keys live only on your site's server — never in a page or a browser. Their full values are shown once, when they are made.",
    nameHint: "Only shown in the console; visitors never see it.",
    publicKeyHint: "Public, and already inside the install code; it grants nothing on its own.",
    embedHint: "On every page of your site, just before the </body> tag.",
    secretKeyHint: "Your site signs its signed-in members with this key, so their name and e-mail reach the chat verified.",
    webhookSecretHint: "When a ticket is opened from a chat, we sign the request we send your site with this key.",
    storageHint: "Images and files sent in chats. Removed together with conversations past the retention period.",
    days: "days",
    statusOn: "Live",
    statusOff: "Off",
    siteCount: (n: number) => `${n} ${n === 1 ? "site" : "sites"}`,
    newButton: "New site",
    copy: "Copy",
    copied: "Copied",
    emptyTitle: "No sites yet",
    rotated: (name: string) => `${name}: new identity key`,
    webhookRotated: (name: string) => `${name}: new webhook key`,
    secretWarn: "This key is shown only once. Save it in your site's server settings — never put it in a page or a browser.",
    webhookWarn: " The webhook key only verifies the ticket requests we send you (covert: CHAT_WEBHOOK_SECRET); don't store it in the same place as the identity key (CHAT_SITE_SECRET).",
    rotatedWarn: " The old key keeps working for 24 hours; swap in the new one before then.",
    revokedWarn: " The old key was revoked at once: signed-in members show as anonymous until your server has the new one.",
    webhookRevokedWarn: " The old key was revoked at once: ticket requests are refused until your server has the new one.",
    savedHide: "Saved it, hide",
    publicKey: "Public key",
    secretKey: "Identity key (secret)",
    webhookSecret: "Webhook signing key (secret)",
    webhookMissing: "Not created yet. Chats can't be turned into tickets until it exists.",
    webhookCreate: "Create webhook key",
    embed: "Snippet for your site",
    origins: "Allowed domains",
    originsHint: "one per line, e.g. https://yoursite.com",
    noOriginsWarn:
      "This site has no allowed domains yet. Until you add your site's domain below, the widget will refuse to load on your page — the snippet can be perfectly correct and still show nothing.",
    noOriginsBadge: "no domain",
    noOriginsNewHint: "Without a domain the widget will not load; you can also add one later from the site's settings.",
    originsSaved: (name: string) => `${name}: allowed domains saved.`,
    identity: "Signed-in members only",
    identityHint:
      "The strongest protection. When on, only members your site has signed (logged-in users) can start a chat; anonymous visitors and bots with a forged Origin header can't get in at all.",
    rotate: "Rotate (old key works for 24 hours)",
    rotateLeaked: "Leaked — revoke now",
    rotateConfirm: (name: string) =>
      `Rotate the identity key for ${name}? The old key keeps working for 24 hours; deploy the new one to your server before then.`,
    rotateLeakedConfirm: (name: string) =>
      `Rotate the identity key for ${name} and revoke the old one IMMEDIATELY? Signed-in members show as anonymous until your server has the new one.`,
    webhookRotateConfirm: (name: string) =>
      `Rotate the webhook key for ${name}? The old key keeps working for 24 hours; deploy the new one to your server before then.`,
    webhookLeakedConfirm: (name: string) =>
      `Rotate the webhook key for ${name} and revoke the old one IMMEDIATELY? Ticket requests are refused until your server has the new one.`,
    previousValid: (when: string) => `The old key keeps working until ${when}.`,
    revokePrevious: "Revoke the old key now",
    revokePreviousConfirm:
      "The old identity key stops working immediately. If your server still uses it, members will show as anonymous. Continue?",
    webhookRevokePreviousConfirm:
      "The old webhook key stops working immediately. If your server still uses it, ticket requests will be refused. Continue?",
    previousRevoked: (name: string) => `${name}: old key revoked.`,
    anonTitle: "Anonymous visitor limit",
    anonIntro:
      "The public key is public, and outside a browser the Origin header can be forged — a botnet rotating IP addresses could fill your inbox. These limits cap how many NEW anonymous visitors and NEW anonymous chats this site accepts within the window. Signed-in members are never counted or refused. Once a limit is reached, anonymous visitors are told the chat is busy and to try again later or sign in. 0 = no limit.",
    anonVisitors: "New anonymous visitors",
    anonConversations: "New anonymous chats",
    anonWindow: "Window (minutes)",
    anonSaved: (name: string) => `${name}: anonymous visitor limit saved.`,
    anonInvalid: "Limits must be whole numbers of 0 or more, and the window 1–1440 minutes.",
    newTitle: "Add a site",
    newIntro: "You'll get two secret keys (identity and webhook) and a one-line snippet to add to your site.",
    name: "Site name",
    create: "Create site",
    ticketUrl: "Support ticket address",
    ticketUrlHint: "When a chat is turned into a ticket, a request signed with the webhook key goes here. For covert: https://yoursite.com/api/chat/ticket",
    ticketUrlSaved: (name: string) => `${name}: ticket address saved.`,
    storage: "File storage",
    nameSaved: (name: string) => `Site renamed to “${name}”.`,
    active: "Site is on",
    activeHint: "While off, the widget does not appear on this site at all. Past conversations are kept.",
    pausedBadge: "off",
    activeSaved: (name: string, on: boolean) => `${name} switched ${on ? "on" : "off"}.`,
    retention: "Keep conversations for (days)",
    retentionHint: "closed conversations older than this are deleted with their messages and files; 0 = never",
    retentionSaved: (name: string) => `${name}: retention saved.`,
    retentionInvalid: "Retention must be a whole number between 0 and 3650 days.",
    deleteTitle: "Delete site",
    deleteIntro:
      "The site is erased with every visitor, conversation, message and file on it. This cannot be undone. If you only want it to stop taking chats, switching it off above is enough.",
    deleteButton: "Delete this site for good",
    deleteOwnerOnly: "Only the workspace owner can delete a site.",
    deleteConfirmTitle: (name: string) => `Delete ${name}?`,
    deleted: (name: string) => `${name} was deleted.`,
    storageUsed: (used: string, cap: string, percent: number) => `${used} of ${cap} used (${percent}%).`,
    storageWarn:
      " This site is nearly full. Once it is, visitors and operators cannot send images here; deleting old chats or shortening the retention window frees space.",
  },
  widget: {
    endedShown: "How long a finished chat stays for the visitor",
    endedShownHint: "Once a chat is resolved or closed and this much time has passed, a returning visitor gets an empty widget and what they write starts a new chat. The old conversation stays as it was in the console and History. Visitors can also start a new chat at once with the button under a finished one.",
    endedShownOptions: { none: "Remove at once", "1h": "1 hour", "24h": "24 hours", "7d": "7 days", always: "Always show" },
    preview: "Preview",
    welcome: "Greeting",
    title: "Title",
    subtitle: "Subtitle",
    greeting: "Greeting message",
    greetingHint: "the first bubble in an empty chat",
    placeholder: "Hint in the message box",
    quickTitle: "Quick reply buttons",
    quickIntro: (max: number) =>
      `Shown as topic buttons when the chat first opens (up to ${max}). The button clicked is sent as the first message and added to the conversation as a tag.`,
    quickNone: "No buttons yet.",
    quickNew: "New button",
    quickExample: "e.g. Payment",
    up: (q: string) => `Move ${q} up`,
    down: (q: string) => `Move ${q} down`,
    remove: (q: string) => `Remove ${q}`,
    look: "Look",
    primary: "Main colour",
    onPrimary: "Text on the main colour",
    position: "Position",
    right: "Bottom right",
    left: "Bottom left",
    theme: "Theme",
    themeAuto: "Follow the visitor's device",
    themeLight: "Always light",
    themeDark: "Always dark",
    offsetX: "Distance from the edge, horizontal (px)",
    offsetY: "Distance from the edge, vertical (px)",
    language: "Language customers see",
    langTr: "Turkish",
    langEn: "English",
    features: "Features",
    attachments: "Image sending",
    attachmentsHint: "Visitors can send screenshots and images.",
    rating: "Ask for a rating at the end",
    ratingHint: "Asks for 1–5 stars when the conversation is marked resolved.",
    typing: "Show “typing…”",
    typingHint: "The visitor sees when an operator is typing.",
    sound: "Notification sound",
    soundHint: "Plays a short sound in the visitor's browser on a new message.",
    contactAsk: "Ask visitors for a name and e-mail",
    contactAskOff: "Don't ask",
    contactAskOptional: "Optional — ask after the first message",
    contactAskRequired: "Required — no chat without it",
    contactAskHint: {
      off: "Visitors start chatting without entering anything. If they write and leave, you have no address to reply to.",
      optional: "After their first message the chat asks for an e-mail address; they can answer “Not now”.",
      required: "A name and e-mail form comes before the first message. Fewer people start a chat, but you can reach all of them.",
    },
    contactAskNote: "Members signed in on your site with a verified identity are never asked, whichever you pick.",
    saved: "Saved. Visitors see the change when they reload the page.",
    faqTitle: "Frequently asked questions",
    faqIntro: (max: number) => `Shown as expandable questions under the greeting while the chat is empty (up to ${max}). Many people find their answer here.`,
    faqNone: "No questions yet.",
    faqQuestion: "Question",
    faqAnswer: "Answer",
    faqAdd: "Add question",
    presetTitle: "Theme preset",
    presetIntro:
      "Pick the widget's shell: header, bubbles, launcher, corners, type and opening motion. Your colours, language, texts and light/dark setting apply to every preset.",
    applyColours: "Also apply the preset's suggested colours when picking",
    applySuggested: (name: string) => `Apply the ${name} preset's suggested colours`,
    presets: {
      classic: { name: "Classic", desc: "Today's look: coloured header, filled bubbles, round button." },
      paper: { name: "Paper", desc: "Flat, shadowless and calm, with hairlines and an underlined input." },
      neon: { name: "Neon", desc: "Glowing edges, chamfered corners, a grid behind the chat; for gaming and tech sites." },
      glass: { name: "Glass", desc: "A frosted, translucent panel; the header fades softly into the chat." },
      bubble: { name: "Bubble", desc: "Rounded type, pill bubbles and a springy open." },
      terminal: { name: "Terminal", desc: "Monospace and no bubbles; messages read like a command log." },
      corporate: { name: "Corporate", desc: "A two-tier colour band, a tight layout and a square button." },
      editorial: { name: "Editorial", desc: "Serif masthead, cream paper, rules instead of bubbles; an interview layout." },
      brutal: { name: "Brutal", desc: "Thick black outlines, hard shadows and a pill button that presses in." },
      dock: { name: "Side panel", desc: "A full-height panel against the screen's edge, opened by a vertical tab." },
      forge: { name: "Steel", desc: "A brushed-steel header, an ice-blue edge light, chamfered corners; for dark gaming sites." },
      aurora: { name: "Aurora", desc: "AgentlyDesk's own: a drifting navy gradient header, one marigold accent and speech-bubble corners." },
    },
    fineTune: "Fine-tuning",
    fineTuneIntro: "Small changes on top of the preset. Anything left on default uses the preset's own value.",
    radius: "Corner roundness",
    radiusDefault: "Preset default",
    font: "Font",
    fontPreset: "Preset default",
    fontSystem: "System (plain)",
    fontRounded: "Rounded",
    fontSerif: "Serif",
    fontMono: "Monospace",
    fontHint: "Uses the fonts already on the visitor's device; no web fonts are loaded.",
    launcher: "Launcher",
    launcherPreset: "Preset default",
    launcherCircle: "Round",
    launcherSquare: "Square",
    launcherPill: "Pill with text",
    launcherText: "Launcher text",
    launcherTextHint: "Left empty, it says “Sohbet” on a Turkish widget and “Chat” on an English one.",
  },
  canned: {
    listTitle: "Your saved replies",
    listIntro: "Type “/” in the reply box to open them; the most used are listed first.",
    none: "No saved replies yet.",
    newTitle: "New saved reply",
    shortcut: "Shortcut",
    shortcutHint: (s: string) => `type “/${s || "shortcut"}”`,
    title: "Title",
    body: "Text",
    scope: "Where it's used",
    allSites: "All sites",
    onlySite: (name: string) => `Only ${name}`,
    used: (n: number) => (n > 0 ? `Used ${n} times` : "Not used yet"),
    saved: (s: string) => `/${s} saved.`,
    added: (s: string) => `/${s} added.`,
    deleted: (s: string) => `/${s} deleted.`,
    confirmDelete: (s: string) => `Delete /${s}?`,
  },
  triggers: {
    listTitle: "Rules on this site",
    listIntro: "The widget shows these messages on its own, before the visitor writes.",
    none: "No automatic messages on this site.",
    newTitle: "New automatic message",
    name: "Name",
    nameHint: "only the team sees it",
    when: "When",
    pageLoad: "When the page opens",
    timeOnPage: "After some time on the page",
    exitIntent: "When they're about to leave",
    delay: "Delay (seconds)",
    pages: "On which pages",
    pagesHint: "part of the address; empty means all",
    pagesExample: "e.g. /setup",
    message: "Message",
    onlyOnline: "Only while the team is online",
    onlyOnlineHint: "Keep this on so the widget doesn't promise a quick answer out of hours.",
    describe: (where: string | null, when: string, online: boolean) =>
      `On ${where ? `pages whose address contains “${where}”` : "every page"}, ${when}${online ? ", only while the team is online" : ""}.`,
    whenTime: (s: number) => `after ${s} seconds`,
    whenExit: "when they're about to leave",
    whenLoad: (s: number) => (s > 0 ? `${s} seconds after it opens` : "as soon as it opens"),
    toggle: (name: string, on: boolean) => `${name} ${on ? "on" : "off"}`,
    saved: (name: string) => `“${name}” saved.`,
    added: (name: string) => `“${name}” added.`,
    deleted: (name: string) => `“${name}” deleted.`,
    confirmDelete: (name: string) => `Delete “${name}”?`,
  },
  hours: {
    weekTitle: "Weekly hours",
    weekHint: "On ticked days you show as online between these times. A day can have more than one range.",
    addHoliday: "Add a day off",
    addHolidayHint: "You show as closed that day, whatever the hours say.",
    holidayList: "Days off",
    title: "Opening hours",
    alwaysOpen: "Always open",
    alwaysOpenHint: "When on, the widget shows as online at every hour.",
    timezone: "Time zone",
    days: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"],
    closed: "Closed",
    from: (day: string) => `${day} from`,
    to: (day: string) => `${day} to`,
    removeRange: (day: string) => `Remove ${day} range`,
    addRange: "Add a range",
    fillIn: (day: string) => `${day}: fill in the times.`,
    order: (day: string) => `${day}: the start must be before the end.`,
    holidays: "Holidays",
    holidaysIntro: "Shown as closed on these dates, whatever the hours say.",
    noHolidays: "No holidays.",
    date: "Date",
    removeDate: (d: string) => `Remove ${d}`,
    whenClosed: "While closed",
    offlineMessage: "Message shown to visitors",
    offlineEmail: "E-mail that receives messages left",
    saved: "Working hours saved.",
  },
  team: {
    workspaceNameHint: "Shown at the top of the console and in e-mails; customers never see it.",
    autoRow: "How chats are handed out",
    members: "Team members",
    you: "(you)",
    twoFactorOn: "2FA on",
    twoFactorOff: "2FA not set up",
    inactive: "Inactive",
    sitesVisible: "Sites they can see",
    allSites: "Sees every site.",
    roleOf: (name: string) => `${name}'s role`,
    deactivate: "Deactivate",
    activate: "Activate",
    deactivated: (name: string) => `${name} was deactivated.`,
    activated: (name: string) => `${name} is active again.`,
    roleChanged: (name: string) => `${name}: role changed.`,
    sitesChanged: (name: string) => `${name}: sites updated.`,
    addTitle: "Add an operator",
    addIntro: "They set up two-step verification the first time they sign in.",
    name: "Name",
    email: "E-mail",
    tempPassword: "Temporary password",
    role: "Role",
    roleAgent: "Agent (chosen sites only)",
    roleAdmin: "Admin (all sites and settings)",
    sitesToSee: "Sites they'll see",
    add: "Add operator",
    added: (name: string) => `${name} was added. Send them the temporary password; they'll set up two-step verification on first sign-in.`,
    autoTitle: "Distributing new conversations",
    autoIntro: "Only operators set to “online” with the console open receive chats. If nobody is, the chat waits in the queue.",
    autoOff: "Don't assign automatically; leave them in the queue",
    autoBalanced: "Share among online operators (fewest open chats first)",
    autoSaved: "Distribution setting saved.",
    workspaceTitle: "Workspace",
    workspaceName: "Workspace name",
    workspaceSaved: "Workspace name saved.",
    inviteByMail: "Let them choose their own password",
    inviteByMailHint: "They're e-mailed a link to set a password. Switch this off to type a temporary password and pass it on yourself.",
    invited: (name: string, email: string) => `${name} was added; the invite went to ${email}.`,
    inviteMailFailed: (name: string) =>
      `${name} was added, but the invite e-mail failed. Use “Reset password” on their row to send the link again.`,
    editMember: "Edit",
    editTitle: (name: string) => `${name}: account details`,
    editBody: "Changing the e-mail signs them out everywhere; they sign in with the new address.",
    memberSaved: (name: string) => `${name} was updated.`,
    resetPassword: "Reset password",
    resetTitle: (name: string) => `${name}: reset password`,
    resetBody: "Send them a link to choose a new password, or type a temporary one. Either way their two-step verification stays as it is.",
    resetSendLink: "E-mail them a link",
    resetLinkSent: (email: string) => `A link to choose a new password went to ${email}.`,
    resetSetTemp: "Type a temporary password",
    resetTempTitle: (name: string) => `${name}: temporary password`,
    resetTempSaved: (name: string) => `Temporary password saved for ${name}; they've been signed out everywhere.`,
    reset2fa: "Reset 2FA",
    reset2faTitle: (name: string) => `Reset two-step verification for ${name}?`,
    reset2faBody: "For someone who lost both their phone and their recovery codes. They're signed out everywhere and set it up again at the next sign-in.",
    reset2faDone: (name: string) => `${name}: two-step verification reset; they'll set it up again on sign-in.`,
    deleteMember: "Delete",
    deleteMemberTitle: (name: string) => `Delete ${name}?`,
    deleteMemberBody: "The account is removed for good. Their messages stay in the conversations, and their open chats become unassigned. To switch the account off for a while, “Deactivate” is enough.",
    memberDeleted: (name: string) => `${name} was deleted.`,
    moreActions: (name: string) => `More actions for ${name}`,
  },
  history: {
    title: "History",
    intro: "Resolved and closed conversations. Searches inside the messages too.",
    search: "Search messages, names or e-mails",
    from: "From",
    to: "To",
    operator: "Operator",
    anyone: "Anyone",
    rating: "Rating",
    anyRating: "All",
    stars: (n: number) => `${n} stars`,
    noRating: "Not rated",
    empty: "No conversations match these filters.",
    more: "Show more",
    closedAt: (d: string) => `Closed ${d}`,
    tagPlaceholder: "e.g. refund",
    exported: (n: number) => `${count(n, "conversation", "conversations")} exported.`,
    exportCapped: (n: number) => `The first ${n} conversations were exported. Narrow the dates to get the rest.`,
    csv: {
      id: "No.",
      site: "Site",
      visitor: "Visitor",
      email: "E-mail",
      status: "Status",
      operator: "Operator",
      rating: "Rating",
      tags: "Tags",
      started: "Started",
      closed: "Closed",
    },
  },
  stats: {
    title: "Statistics",
    range: "Range",
    today: "Today",
    week: "Last 7 days",
    month: "Last 30 days",
    conversations: "Conversations",
    unanswered: "Never answered",
    firstResponse: "First response (average)",
    firstResponseMedian: (x: string) => `median ${x}`,
    resolution: "Time to resolve (average)",
    rating: "Average rating",
    ratingCount: (n: number) => `${n} ratings`,
    perDay: "Conversations per day",
    busiest: "Busiest hours",
    busiestHint: "Darker squares mean more conversations started on that day and hour.",
    team: "Team",
    operator: "Operator",
    handled: "Handled",
    avgFirst: "First response",
    avgRating: "Rating",
    topics: "Topics",
    topicsHint: "From quick reply buttons and tags.",
    comments: "Latest comments",
    noData: "No data in this range.",
    none: "—",
    weekdays: ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"],
    tableView: "Show as a table",
    date: "Date",
    count: "Count",
    seconds: (n: number) => `${n}s`,
    minutes: (n: number) => `${n}m`,
    hoursMinutes: (h: number, m: number) => `${h}h ${m}m`,
    distribution: "Rating breakdown",
    custom: "Custom range",
    from: "From",
    to: "To",
    customInvalid: "The end can't be before the start, and a range can cover at most 366 days.",
    csvMetric: "Metric",
    csvValue: "Value",
  },
  spam: {
    rulesTitle: "Message rules",
    wordsTitle: "Blocked words",
    wordsIntro: "Visitor messages containing these words or phrases aren't sent; the visitor is told why in their language. The list stays on the server and never reaches a visitor's browser.",
    wordsLabel: "Words",
    wordsHint: "one word or phrase per line; case-insensitive, whole words only",
    links: "Block links",
    linksHint: "Visitor messages may not contain web addresses (e.g. site.com).",
    repeats: "Repeat limit",
    repeatsHint: "If the same message was sent this many times within a minute, further copies are refused. 0 turns it off.",
    saved: "Spam settings saved.",
    bansTitle: "Banned visitors",
    bansIntro: "A banned visitor can't open the widget. If someone was banned by mistake, lift it here.",
    bansNone: "Nobody is banned.",
    banReason: (reason: string) => `Reason: ${reason}`,
    bannedAt: (date: string) => `Banned ${date}`,
    bansFailed: "Couldn't load the ban list.",
  },
  audit: {
    intro: "Who did what to the settings and accounts, and when. Message contents are never kept here.",
    filter: "Action",
    all: "All",
    when: "When",
    who: "Who",
    what: "What",
    system: "System",
    platform: "AgentlyDesk",
    empty: "No entries for this filter.",
    more: "Older entries",
    actor: "Person",
    targetColumn: "Target",
    anyone: "Anyone",
    from: "From",
    to: "To",
    exported: (n: number) => `${count(n, "entry", "entries")} exported.`,
    exportCapped: (n: number) => `The first ${n} entries were exported. Narrow the dates to get the rest.`,
    actions: {
      "operator.login": "Signed in",
      "operator.login_dev_no_2fa": "Development sign-in (no 2FA)",
      "operator.login_failed": "Failed sign-in attempt",
      "operator.logout": "Signed out",
      "operator.2fa_enabled": "Set up two-step verification",
      "operator.2fa_recovery_used": "Signed in with a recovery code",
      "operator.2fa_failed": "Wrong two-step verification code",
      "operator.password_changed": "Changed their password",
      "operator.recovery_codes_regenerated": "Regenerated recovery codes",
      "operator.created": "Added an operator",
      "operator.updated": "Updated an operator",
      "operator.deactivated": "Deactivated an operator",
      "operator.sites_changed": "Changed an operator's sites",
      "site.created": "Created a site",
      "site.updated": "Changed site settings",
      "site.rotate_key": "Rotated the identity key",
      "site.revoke_previous_key": "Revoked the old identity key",
      "site.rotate_webhook_secret": "Rotated the webhook key",
      "site.revoke_previous_webhook_secret": "Revoked the old webhook key",
      "site.origins_changed": "Changed allowed domains",
      "visitor.banned": "Banned a visitor",
      "visitor.unbanned": "Lifted a ban",
      "visitor.purged": "Erased all of a visitor's data",
      "conversation.deleted": "Deleted a conversation",
      "conversation.ticket_created": "Opened a ticket from a chat",
      "workspace.updated": "Changed team settings",
      "workspace.created": "Workspace opened",
      "workspace.plan_changed": "Plan changed",
      "workspace.deleted": "Deleted a workspace",
      "operator.invite_sent": "Invite sent",
      "telegram.group_linked": "Telegram group linked",
      "telegram.group_unlinked": "Telegram group unlinked",
      "telegram.operator_linked": "Telegram account linked",
      "telegram.operator_unlinked": "Telegram account unlinked",
      "operator.password_reset_requested": "Asked for a password reset",
      "operator.password_reset_sent": "Sent a password reset link",
      "operator.2fa_reset": "Reset two-step verification",
      "operator.deleted": "Deleted an operator",
      "site.deleted": "Deleted a site",
      "visitor.updated": "Corrected a visitor's details",
    } as Record<string, string>,
    target: (type: string, id: string, name?: string | null) => (name ? `${type}: ${name} (#${id})` : `${type} #${id}`),
  },
  earlyAccess: {
    title: "Early access",
    intro: "Requests left on AgentlyDesk's marketing site, newest first.",
    filterLabel: "Filter by status",
    statusAll: "All",
    statuses: {
      new: "New",
      contacted: "Contacted",
      invited: "Invited",
      rejected: "Rejected",
    } as Record<string, string>,
    date: "Date",
    name: "Name",
    email: "E-mail",
    company: "Company",
    website: "Website",
    teamSize: "Team size",
    plan: "Plan",
    message: "Message",
    status: "Status",
    none: "—",
    showMessage: "Show message",
    hideMessage: "Hide message",
    empty: "No requests match this filter.",
    loadFailed: "Couldn't load the requests.",
    updateFailed: "Couldn't update the status.",
    approve: "Approve",
    approveTitle: (name: string) => `Open an account for ${name}?`,
    approveIntro: "This creates the workspace and its owner, and e-mails a link to set a password.",
    approvePlan: "Starting plan",
    approveSubmit: "Create the account and send the invite",
    approving: "Creating…",
    approved: (email: string) => `Account created; the invite went to ${email}.`,
    toCustomers: "Go to Customers",
    resend: "Resend the invite",
    resent: (email: string) => `The invite was sent to ${email} again.`,
    errSmtp: "SMTP is not configured, so no invite can be sent.",
    errAlreadyOperator: "That e-mail address already belongs to an account.",
    errNotApprovable: "This request can no longer be approved.",
    errSlugConflict: "Couldn't make an address from that name. Change the company name and try again.",
    errMailFailed: "The account was created but the invite e-mail failed. Resend the invite.",
    approveFailed: "Couldn't create the account.",
  },
  plan: {
    names: { free: "Free", starter: "Starter", team: "Team", unlimited: "Unlimited" } as Record<string, string>,
    limitTitle: "Plan limit",
    limits: {
      sites: "You've reached your plan's site limit.",
      operators: "You've reached your plan's operator limit.",
      themes: "Ready-made themes and fine-tuning aren't in your plan.",
      cannedReplies: "Saved replies aren't in your plan.",
      verifiedIdentity: "Verified identity isn't in your plan.",
      ticketWebhook: "The ticket webhook isn't in your plan.",
      auditLog: "The audit log isn't in your plan.",
    } as Record<string, string>,
    upgrade: "Upgrade the plan",
    askOwner: "Ask the workspace owner to upgrade.",
    needsPlan: (name: string) => `On ${name}`,
    suspendedTitle: "Workspace suspended",
    suspendedBody: "This workspace is unavailable right now, and the chat widget is closed to visitors.",
    suspendedOwner: "Settle the payment or get in touch with us to carry on.",
    suspendedOther: "Talk to the workspace owner to carry on.",
    goBilling: "Go to billing",
  },
  billing: {
    intro: "Your plan, your usage and your payments.",
    loadFailed: "Couldn't load your billing details.",
    notConfiguredTitle: "Payments aren't switched on",
    notConfiguredBody: "Card payments are off in this installation. Get in touch with us to change plan.",
    planTitle: "Your plan",
    statusActive: "active",
    statusPastDue: "payment overdue",
    statusCanceled: "canceled",
    statusSuspended: "suspended",
    graceNote: (stored: string, until: string) => `${stored} — payment overdue, grace ends ${until}.`,
    downgradedNote: (stored: string) => `Your ${stored} payment didn't go through, so Free rules apply for now.`,
    suspendedNote: "The workspace is suspended, so Free rules apply for now.",
    renews: (date: string) => `Renews ${date}`,
    perMonth: (price: string) => `${price} a month`,
    usageTitle: "Usage",
    sites: "Sites",
    operators: "Operators",
    ofLimit: (used: number, limit: string) => `${used} / ${limit}`,
    upgradeTitle: "Change plan",
    upgradeIntro: "One flat price per workspace — nothing is charged per operator.",
    upgradeStarter: "Upgrade to Starter",
    upgradeTeam: "Upgrade to Team",
    manageTitle: "Payments and invoices",
    manage: "Open the billing page",
    manageHint: "Cards, invoices and plan changes happen on Stripe's own page.",
    checkoutSuccess: "Payment received. It can take a moment for your plan to catch up.",
    checkoutCancel: "The payment wasn't completed, so nothing changed.",
    refresh: "Refresh",
    errSubscriptionExists: "You already have a subscription. Change the plan from the billing page.",
    errNoCustomer: "There's no payment record yet. Pick a plan first.",
    errNoSubscription: "There's no active subscription. Move to a paid plan first.",
    errNotConfigured: "The payment provider isn't configured in this installation.",
    errProvider: "Couldn't reach the payment provider. Try again shortly.",
  },
  customers: {
    title: "Customers",
    intro: "Every workspace, its plan and its owner. Newest first.",
    loadFailed: "Couldn't load the workspaces.",
    empty: "No workspaces yet.",
    name: "Workspace",
    owner: "Owner",
    plan: "Plan",
    usage: "Usage",
    created: "Created",
    actions: "Actions",
    noOwner: "—",
    usageCell: (sites: number, operators: number) =>
      `${count(sites, "site", "sites")} · ${count(operators, "operator", "operators")}`,
    save: "Save",
    suspend: "Suspend",
    unsuspend: "Lift the suspension",
    confirmSuspend: (name: string) => `Suspend ${name}? The widget and the console both close.`,
    stripeWarning: "There's a Stripe subscription — the next Stripe event can undo this change.",
    saved: (name: string) => `${name} updated.`,
    pausedNote: (sites: number, operators: number) =>
      `${count(sites, "site", "sites")} and ${count(operators, "operator", "operators")} were paused.`,
    resumedNote: (sites: number, operators: number) =>
      `${count(sites, "site", "sites")} and ${count(operators, "operator", "operators")} came back.`,
    updateFailed: "Couldn't update it.",
    resend: "Resend the invite",
    resent: (email: string) => `The invite went to ${email}.`,
    errSmtp: "SMTP is not configured, so no invite can be sent.",
    errNoOwner: "This workspace has no active owner.",
    errAlreadyActive: "The owner's account is already set up, so no invite can be resent.",
    errMailFailed: "The e-mail couldn't be sent.",
    search: "Search workspaces, addresses or e-mails",
    shown: (shown: number, total: number) => `Showing ${shown} of ${count(total, "workspace", "workspaces")}.`,
    noMatch: "No workspace matches that search.",
    newTitle: "New customer",
    newIntro: "Creates the workspace and its owner, and e-mails the owner a link to set a password.",
    newOpen: "Open a new customer",
    workspaceName: "Workspace name",
    ownerName: "Owner's name",
    ownerEmail: "Owner's e-mail",
    create: "Create the account and send the invite",
    creating: "Creating…",
    createdOk: (email: string) => `Account created; the invite went to ${email}.`,
    createdMailFailed: "The account was created but the invite e-mail failed. Use “Resend the invite” in the list.",
    errAlreadyOperator: "That e-mail address already belongs to an account.",
    delete: "Delete",
    deleteTitle: (name: string) => `Delete ${name}?`,
    deleteBody: "The workspace is erased with its sites, operators, every conversation and every file. This cannot be undone.",
    deleted: (name: string) => `${name} was deleted.`,
    errOwnWorkspace: "You can't delete your own workspace.",
    errHasSubscription: "There's an active Stripe subscription. Cancel it first.",
  },
  setPassword: {
    title: "Set your password",
    intro: "Your AgentlyDesk account is ready. Choose a password, then sign in as usual.",
    password: "New password",
    confirm: "Password again",
    rule: "Between 12 and 200 characters.",
    mismatch: "The two passwords don't match.",
    submit: "Save the password",
    saving: "Saving…",
    doneTitle: "Password saved",
    doneBody: "You can sign in now. Your first sign-in sets up two-step verification.",
    goLogin: "Go to the sign-in screen",
    missingToken: "This link looks incomplete. Open the link from the e-mail exactly as it is.",
    weak: "The password must be between 12 and 200 characters.",
    invalid: "This link is invalid or has expired. Ask for a new invite.",
    failed: "Couldn't save the password.",
  },
  errors: {
    not_found: "Not found.",
    invalid_request: "That request wasn't valid.",
    invalid_query: "That query wasn't valid.",
    invalid_input: "Something you entered isn't valid.",
    unauthorized: "You're not signed in, or the session expired. Sign in again.",
    forbidden: "You don't have permission to do that.",
    reauth_required: "Confirm your second factor again to continue.",
    twofa_required: "Set up two-factor authentication before using the console.",
    rate_limited: "Too many requests. Wait a moment and try again.",
    workspace_suspended: "This workspace is suspended.",
    plan_limit: "Your current plan doesn't allow this.",
    internal_error: "Something went wrong on the server.",
    invalid_json: "The server couldn't read that request.",
    body_too_large: "That was too much data to send at once.",
    invalid_credentials: "Wrong e-mail or password.",
    account_locked: (seconds: number) => `The account is locked for a moment. Try again in ${seconds} seconds.`,
    ticket_expired: "The sign-in request expired. Please start again.",
    ticket_invalid: "That sign-in request isn't valid.",
    code_invalid: "That code didn't verify.",
    code_required: "Enter the code.",
    twofa_unreadable: "The server couldn't read your two-factor settings.",
    twofa_already_set: "Two-factor is already set up.",
    twofa_not_enabled: "Two-factor isn't set up on this account.",
    invalid_token: "This link is invalid or has expired.",
    weak_password: "The password must be between 12 and 200 characters.",
    wrong_password: "That current password is wrong.",
    owner_role_required: "Only an owner can create or assign another owner.",
    owner_only_target: "Only an owner can change an owner's account.",
    last_owner: "The last owner can't be deactivated or demoted.",
    email_taken: "That e-mail is already registered.",
    invalid_origin: "That allowed domain isn't valid. Example: https://example.com — no path, no wildcards.",
    webhook_url_invalid: "The ticket address isn't a valid URL.",
    webhook_url_not_https: "The ticket address has to start with https://.",
    webhook_url_ip: "The ticket address has to be a domain name, not an IP address.",
    invalid_widget_config: "One of the widget settings isn't valid.",
    noPreviousKey: "There's no previous key still inside its grace window.",
    unknown_timezone: "That time zone isn't recognised.",
    operator_not_on_site: "That operator isn't assigned to this site.",
    no_visitor_email: "This visitor has no e-mail address on file.",
    smtp_required: "E-mail sending isn't configured.",
    message_too_long: "That message is too long.",
    message_empty: "The message is empty.",
    invalid: "That request wasn't valid.",
    exists: "A ticket was already opened from this chat.",
    inProgress: "A ticket for this chat is being opened right now.",
    notConfigured: "No ticket address is set for this site.",
    webhookSecretMissing: "This site has no webhook signing key. Create one under Settings → Sites.",
    notIdentified: "The visitor isn't signed in on the site.",
    webhookFailed: "The site refused the ticket.",
    attachments_disabled: "File sending is switched off for this site.",
    link_expired: "That link has expired.",
    upload_rejected: "That file wasn't accepted.",
    empty_file: "That file is empty.",
    file_too_large: "A file can't be larger than 10 MB.",
    unsupported_image_type: "PNG, JPG, GIF or WEBP only.",
    conversation_attachment_limit: "No more files can be sent in this chat.",
    storage_full: "This site's file storage is full.",
    avatar_too_large: "An avatar can't be larger than 2 MB.",
    invalid_display_name: "The name has to be 2-40 characters.",
    invalid_range: "That range isn't valid.",
    billing_not_configured: "Payments aren't configured.",
    billing_provider_error: "Couldn't reach the payment provider. Please try again.",
    subscription_exists: "This workspace already has a subscription — make changes from the billing portal.",
    no_customer: "There's no billing account yet.",
    already_operator: "That e-mail already belongs to an operator.",
    not_approvable: "This request can't be approved — it was already invited or rejected.",
    slug_conflict: "The workspace address clashed. Try again.",
    mail_failed: "Couldn't send the e-mail.",
    no_owner: "This workspace has no active owner.",
    already_active: "The owner account is already in use.",
    confirm_mismatch: "The confirmation text doesn't match.",
    cannot_target_self: "You can't do that to your own account.",
    operator_inactive: "A link can't be sent to a deactivated account.",
    message_not_editable: "That message can't be changed.",
    not_message_author: "You can only change your own messages.",
    visitor_verified: "A verified member's details come from the site and can't be changed here.",
    visitor_banned: "This visitor is banned.",
    unsupported_file_type: "Only images, PDFs and plain text (.txt, .log) can be sent.",
    cannot_delete_own_workspace: "You can't delete your own workspace.",
    has_subscription: "This workspace has an active Stripe subscription. Cancel it first.",
  },
};

export const DICTS: Record<Lang, Dict> = { tr, en };

export const DATE_LOCALE: Record<Lang, string> = { tr: "tr-TR", en: "en-GB" };

const STORAGE_KEY = "covertchat.console.locale";

/** Before sign-in there is no operator to ask, so the browser's own language decides. */
export function guessLang(): Lang {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved === "tr" || saved === "en") return saved;
  } catch {
    /* storage blocked */
  }
  return typeof navigator !== "undefined" && navigator.language?.toLowerCase().startsWith("tr") ? "tr" : "en";
}

export function rememberLang(lang: Lang): void {
  try {
    localStorage.setItem(STORAGE_KEY, lang);
  } catch {
    /* storage blocked */
  }
}

export interface I18n {
  lang: Lang;
  t: Dict;
}

export const I18nContext = createContext<I18n>({ lang: "tr", t: tr });

export function useI18n(): I18n {
  return useContext(I18nContext);
}
